import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageCircle,
  Mic,
  Type as TextIcon,
  Star,
  BookOpen,
  Utensils,
  Lightbulb,
  Trophy,
  Target,
  Heart,
  Wine,
  ListChecks,
  HelpCircle,
  Play,
  History,
  RotateCcw,
  X,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import {
  getQuizContent,
  createUnit,
  updateUnit,
  deleteUnit,
  createLesson,
  updateLesson,
  deleteLesson,
  moveDrill,
  createQuestion,
  updateQuestion,
  deleteQuestion,
  createRehearsal,
  updateRehearsal,
  deleteRehearsal,
  CorrectOption,
  QuestionType,
  QuizContent,
  QuizLesson,
  QuizQuestion,
  QuizUnit,
  RehearsalCriterion,
  RehearsalLessonItem,
  LESSON_ICON_CATALOG,
  UNIT_MASCOT_CATALOG,
  // feat-164: curriculum change history + per-item undo.
  getItemHistory,
  getTenantHistory,
  revertCurriculumChange,
  CurriculumHistoryEntry,
  CurriculumEntityType,
  CURRICULUM_ENTITY_TYPE,
  // feat-167: single-entry before/after snapshot.
  getHistorySnapshot,
  CurriculumHistorySnapshot,
  CurriculumSnapshotSide,
  // feat-108 (ADR 0023): Lesson custom-image upload + public-URL resolver.
  uploadLessonImage,
  lessonImagePublicUrl,
  LESSON_IMAGE_ALLOWED_TYPES,
  LESSON_IMAGE_MAX_BYTES,
  // feat-165: explanatory video item.
  updateVideoItem,
  deleteVideoItem,
  VideoItemSummary,
  // feat-166: reading item.
  updateReadingItem,
  deleteReadingItem,
  ReadingItemSummary,
  // feat-235: tracks (level 0) — a separate tab, NOT a restructured tree.
  createTrack,
  getLearnTracks,
  LearnTrack,
  DefaultTrackState,
} from '../../services/quizContentAdminService';
import {
  uploadScenarioImage,
  listRolesWithActivePrompt,
} from '../../services/rehearsalAdminService';
import { config } from '../../config/environment';
import {
  mcOptionsProblem,
  isMcOptionLocked,
  mcOptionPlaceholder,
  blanksCorrectOption,
  isOptionFilled,
} from '../../utils/mcOptions';
import { useTenant } from '../../contexts/TenantContext';
import ScorecardNavigation from '../restaurant/ScorecardNavigation';
// feat-107 Slice A — master-detail editor pieces + shared primitives.
import { QUIZ_TERMS } from './quizTerminology';
import { timeAgo } from './curriculum/relativeTime';
import { APPROVAL_EDITOR_COPY } from './curriculum/approvalCopy';
import { ApprovalGateProvider, useApprovalGate } from './curriculum/approvalGate';
import {
  ItemApprovalProvider,
  type ItemApprovalState,
} from './curriculum/approval/itemApproval';
import {
  LessonApprovalStamp,
  type LessonApprovalRow,
} from './curriculum/approval/LessonApprovalStamp';
import {
  ApprovedFieldMarker,
  ApprovedValueLine,
  PanelApprovalState,
} from './curriculum/approval/itemApproval';
import {
  actorDisplayName,
  getApprovalLessonDetail,
  getApprovalSummary,
  type ApprovalLessonPage,
  type ApprovalSummary,
  type LessonApprovalStatus,
} from '../../services/curriculumApprovalService';
import {
  CHANGED_FIELD_ROW_CLASS,
  ChangedMarker,
  ChangedDot,
} from './curriculumDiffPresentation';
import { glyphForKey } from './curriculum/curriculumGlyphs';
// feat-187 (R1): the Lesson glyph picker + its VARCHAR(10) guardrail.
import { IconPickerPopover, IconSelection } from './IconPickerPopover';
import { normalizeUnitEmoji } from './curriculum/unitEmoji';
import { MasterDetailTree } from './curriculum/MasterDetailTree';
import { Copy as CopyIcon } from 'lucide-react';
import { COPY_OF_DELETED, COPY_OF_LEAD, copyOfInfo, openOriginalTitle } from './curriculum/lessonCopyOfCopy';
import { DrillChildList, DrillItem, DrillMetaPatch } from './curriculum/DrillChildList';
import { QuestionDetailForm } from './curriculum/QuestionDetailForm';
import { DetailTypeControl, DetailItemType } from './curriculum/DetailTypeControl';
import { VideoDetailForm } from './curriculum/VideoDetailForm';
import { ReadingDetailForm } from './curriculum/ReadingDetailForm';
import ContentTimestamps from './curriculum/ContentTimestamps';
import { useAutoSave, PanelSaveWiring } from './curriculum/useAutoSave';
import { ItemDetailFooter, SaveErrorBanner } from './curriculum/ItemDetailFooter';
import { AUTO_SAVE_COPY } from './curriculum/autoSaveCopy';
import { PublishGuardDialog } from './curriculum/PublishGuardDialog';
import { MoveDrillDialog } from './curriculum/MoveDrillDialog';
import {
  PublishGuardReport,
  needsPublishConfirmation,
  reportForDrill,
  reportForUnit,
} from './curriculum/publishGuard';
import { applyRowPatches } from './curriculum/rowPatches';
import { LessonScheduleControl } from './curriculum/LessonScheduleControl';
import LessonSkipDripToggle from './curriculum/LessonSkipDripToggle';
import { ImporterContext, ImporterRenderer } from './curriculum/importerHook';
// feat-107 Slice B (frontend-b) wire-in — the paste-from-Notion launcher.
import { PasteImportLauncher } from './curriculum/importer/PasteImportLauncher';
// feat-235 — the Tracks tab and the Lessons tab's read-only membership strip.
import { TracksTab } from './curriculum/tracks/TracksTab';
import { LessonTracksStrip } from './curriculum/tracks/LessonTracksStrip';
import { suppressesTrackCreation } from './curriculum/tracks/DefaultTrackAlert';

/**
 * feat-078: open the rehearsal test harness in a SEPARATE browser window so the
 * author can edit curriculum and run the harness side-by-side. The window is
 * named per-rehearsal so re-clicking Test focuses the existing window instead of
 * stacking duplicates. Cosmetic header meta (name, pinned version) rides along
 * as query params; the harness loads its own suite/grading from the id.
 *
 * The `popup=yes` + explicit width/height features string forces a real OS
 * window (Chrome opens a feature-less window.open as a TAB). Centered on the
 * opener so it lands next to the editor rather than off-screen.
 */
const HARNESS_WIN_WIDTH = 1100;
const HARNESS_WIN_HEIGHT = 950;

function openTestHarnessWindow(
  rehearsalId: string,
  name: string,
  pinnedVersionNumber: number | null,
): void {
  const params = new URLSearchParams();
  if (name) params.set('name', name);
  if (pinnedVersionNumber != null) params.set('v', String(pinnedVersionNumber));
  const qs = params.toString();
  const url = `/quiz-management/rehearsal-test/${rehearsalId}${qs ? `?${qs}` : ''}`;

  // Center on the opener window (fall back to 0 when geometry is unavailable).
  const left = Math.round(
    (window.screenX ?? 0) +
      Math.max(0, ((window.outerWidth || HARNESS_WIN_WIDTH) - HARNESS_WIN_WIDTH) / 2),
  );
  const top = Math.round(
    (window.screenY ?? 0) +
      Math.max(0, ((window.outerHeight || HARNESS_WIN_HEIGHT) - HARNESS_WIN_HEIGHT) / 2),
  );
  const features = `popup=yes,width=${HARNESS_WIN_WIDTH},height=${HARNESS_WIN_HEIGHT},left=${left},top=${top}`;

  window.open(url, `rehearsal-test-${rehearsalId}`, features);
}

// ============================================================
// Helpers
// ============================================================

const OPTION_LETTERS: CorrectOption[] = ['a', 'b', 'c', 'd'];

function usePatchedState<T>(initial: T): [T, (patch: Partial<T>) => void, (next: T) => void] {
  const [state, setState] = useState<T>(initial);
  const patch = (p: Partial<T>) => setState((s) => ({ ...s, ...p }));
  return [state, patch, setState];
}

// ---------- feat-033 shared authoring controls ----------

const QUESTION_TYPE_OPTIONS: { value: QuestionType; label: string }[] = [
  { value: 'multiple_choice', label: 'Multiple choice' },
  { value: 'fill_blank', label: 'Fill in the blank' },
];

/** Segmented control toggling a question between the classic A/B/C/D drill and
 *  the feat-033 fill-in-the-blank type (which reuses the same option fields as
 *  MC-fallback distractors). */
const QuestionTypeSelector: React.FC<{
  value: QuestionType;
  onChange: (next: QuestionType) => void;
}> = ({ value, onChange }) => (
  <div className="inline-flex rounded border border-gray-300 overflow-hidden text-xs">
    {QUESTION_TYPE_OPTIONS.map((opt) => (
      <button
        key={opt.value}
        type="button"
        onClick={() => onChange(opt.value)}
        aria-pressed={value === opt.value}
        className={`px-2 py-1 ${
          value === opt.value
            ? 'bg-blue-600 text-white'
            : 'bg-white text-gray-700 hover:bg-gray-50'
        }`}
      >
        {opt.label}
      </button>
    ))}
  </div>
);

/** Guidance shown only in fill-blank mode explaining the `___` marker + how the
 *  option fields double as the answer + MC-fallback distractors (ADR 0001 §D1).
 *  Surfaces a soft, non-blocking warning if the sentence has no blank marker. */
const FillBlankHint: React.FC<{ questionText: string }> = ({ questionText }) => {
  const missingBlank = !questionText.includes('___');
  return (
    <div className="mb-2 space-y-1">
      <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
        Fill in the blank: write the sentence with <code>___</code> (three
        underscores) where the blank goes. The <strong>correct</strong> option
        below is the word/phrase that fills it — it's matched against the
        learner's typed or spoken answer. The other three options are the
        multiple-choice fallback shown after a wrong attempt.
      </div>
      {missingBlank && (
        <div className="text-xs text-amber-900">
          Tip: the question text has no <code>___</code> blank marker yet.
        </div>
      )}
    </div>
  );
};

/** feat-075 (ADR 0011): accepted-variants textarea for fill-blank questions.
 *  The value maps 1:1 to the `blank_accepted_variants` string the admin API
 *  persists (newline-delimited, one variant per line). Rendered ONLY for
 *  fill-blank questions (caller gates on `question_type === 'fill_blank'`).
 *  The server normalizes/dedupes/drops-the-canonical and is the authoritative
 *  validator (≤10 variants, ≤64 chars each); this surfaces a soft, non-blocking
 *  warning when those limits look exceeded but never blocks save. */
const VARIANTS_MAX_COUNT = 10;
const VARIANTS_MAX_LINE_LEN = 64;

const AcceptedVariantsField: React.FC<{
  value: string;
  onChange: (next: string) => void;
}> = ({ value, onChange }) => {
  const lines = value
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  const tooMany = lines.length > VARIANTS_MAX_COUNT;
  const longLines = lines.filter((l) => l.length > VARIANTS_MAX_LINE_LEN);
  return (
    <div className="mb-3">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Accepted variants
      </label>
      <textarea
        aria-label="Accepted variants"
        className="border rounded px-2 py-1 text-sm w-full"
        rows={3}
        placeholder={'ok\nokay\nalright'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <div className="text-xs text-gray-500 mt-1">
        One variant per line. The canonical answer above is what learners see
        when they fall back to multiple choice — variants here are only used to
        grade typed and spoken answers.
      </div>
      {(tooMany || longLines.length > 0) && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mt-1">
          {tooMany && (
            <div>
              {lines.length} variants entered — the server keeps at most{' '}
              {VARIANTS_MAX_COUNT} (extras may be rejected on save).
            </div>
          )}
          {longLines.length > 0 && (
            <div>
              {longLines.length === 1 ? 'A variant is' : `${longLines.length} variants are`}{' '}
              over {VARIANTS_MAX_LINE_LEN} characters — the server may reject
              over-long entries on save.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/** Dropdown over a frozen authoring catalog (lesson icon / unit mascot). The
 *  empty option maps to NULL — the app then falls back (icon→emoji, mascot→none),
 *  per ADR 0001 §OQ-D. */
const CatalogSelect: React.FC<{
  label: string;
  emptyLabel: string;
  catalog: ReadonlyArray<{ key: string; label: string }>;
  value: string | null;
  onChange: (next: string | null) => void;
}> = ({ label, emptyLabel, catalog, value, onChange }) => (
  <label className="text-xs text-gray-600 flex items-center gap-1">
    {label}
    <select
      className="border rounded px-2 py-1 text-sm"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
    >
      <option value="">{emptyLabel}</option>
      {catalog.map((entry) => (
        <option key={entry.key} value={entry.key}>
          {entry.label}
        </option>
      ))}
    </select>
  </label>
);

/** Per-question XP input. Empty ⇒ NULL ⇒ the backend resolves a per-type
 *  default at read time (ADR 0001 §OQ-C), so the placeholder reads "Default". */
const XpValueField: React.FC<{
  value: number | null;
  onChange: (next: number | null) => void;
}> = ({ value, onChange }) => (
  <label className="text-xs text-gray-600 flex items-center gap-1">
    XP
    <input
      type="number"
      min={0}
      max={1000}
      placeholder="Default"
      title="Per-question XP. Leave blank to use the per-type default."
      className="border rounded px-2 py-1 text-sm w-20"
      value={value ?? ''}
      onChange={(e) => {
        const v = e.target.value.trim();
        if (v === '') {
          onChange(null);
        } else {
          const n = parseInt(v, 10);
          onChange(Number.isFinite(n) ? n : null);
        }
      }}
    />
  </label>
);

/** Lesson-icon glyphs (lucide). Keyed by the frozen LESSON_ICON_CATALOG keys —
 *  the labels live in the catalog (single source); this map only binds each key
 *  to its glyph. A key with no glyph falls back to HelpCircle so legacy/stale
 *  data never crashes the editor. */
const ICON_GLYPHS: Record<string, React.ComponentType<{ className?: string }>> = {
  chat_bubble: MessageCircle,
  microphone: Mic,
  text_t: TextIcon,
  star: Star,
  book: BookOpen,
  utensils: Utensils,
  lightbulb: Lightbulb,
  trophy: Trophy,
  target: Target,
  heart: Heart,
  wine_glass: Wine,
  checklist: ListChecks,
};

/** Compact, read-only lesson-icon indicator shown in the collapsed lesson header
 *  (replaces the old free-form emoji input there). Click → expand to edit. Shows
 *  the catalog glyph when icon_key is set, else the free-form emoji, else 🍔. */
const LessonIconIndicator: React.FC<{
  iconKey: string | null;
  emoji: string;
  onClick: () => void;
}> = ({ iconKey, emoji, onClick }) => {
  const Glyph = iconKey ? ICON_GLYPHS[iconKey] ?? HelpCircle : null;
  return (
    <button
      type="button"
      onClick={onClick}
      title="Lesson icon — expand to edit"
      className="border rounded w-12 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 shrink-0"
    >
      {Glyph ? <Glyph className="h-4 w-4" /> : <span className="text-sm">{emoji || '🍔'}</span>}
    </button>
  );
};

/** Visual chip grid over the frozen 12-key lesson-icon catalog (ADR 0001 §OQ-D +
 *  design-lead Slice C guidance), with the existing free-form emoji kept as a
 *  secondary fallback. Precedence: icon_key set → app uses the catalog icon;
 *  icon_key null → app falls back to emoji. Clicking the selected tile clears it. */
const IconKeyPicker: React.FC<{
  iconKey: string | null;
  emoji: string;
  onIconKey: (next: string | null) => void;
  onEmoji: (next: string) => void;
}> = ({ iconKey, emoji, onIconKey, onEmoji }) => {
  const usingIcon = iconKey != null && iconKey !== '';
  const staleKey = usingIcon && !(iconKey in ICON_GLYPHS);
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">Lesson icon</label>
      <div className="grid grid-cols-6 gap-2 mb-1">
        {LESSON_ICON_CATALOG.map(({ key, label }) => {
          const Glyph = ICON_GLYPHS[key] ?? HelpCircle;
          const selected = iconKey === key;
          return (
            <button
              key={key}
              type="button"
              title={label}
              aria-pressed={selected}
              onClick={() => onIconKey(selected ? null : key)}
              className={`flex flex-col items-center gap-1 border rounded px-1 py-2 text-gray-600 hover:bg-gray-50 ${
                selected
                  ? 'border-blue-600 ring-1 ring-blue-600 bg-blue-50'
                  : 'border-gray-200'
              }`}
            >
              <Glyph className="h-5 w-5" />
              <span className="text-[10px] leading-none truncate w-full text-center">
                {label}
              </span>
            </button>
          );
        })}
      </div>
      {staleKey && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-1 flex items-center justify-between gap-2">
          <span>
            Unknown icon key <code>{iconKey}</code> (not in the catalog). Pick one
            above to replace it.
          </span>
          <button
            type="button"
            onClick={() => onIconKey(null)}
            className="text-blue-700 hover:text-blue-900 shrink-0"
          >
            Clear
          </button>
        </div>
      )}
      <div className="flex items-center gap-2">
        <input
          className={`border rounded px-2 py-1 text-sm w-12 text-center ${
            usingIcon ? 'opacity-40' : ''
          }`}
          placeholder="🍔"
          value={emoji}
          disabled={usingIcon}
          onChange={(e) => onEmoji(e.target.value)}
        />
        <span className="text-xs text-gray-500">
          {usingIcon
            ? 'Using catalog icon — click the selected tile again to clear it and use a custom emoji.'
            : '…or paste a custom emoji (used when no catalog icon is picked).'}
        </span>
      </div>
    </div>
  );
};

// ============================================================
// Question editor
// ============================================================

/** feat-346: an editor draft may have NO correct pick — blanking the picked MC
 *  option clears it, and the author must choose again before saving. */
type QuestionRowDraft = Omit<QuizQuestion, 'correct_option'> & { correct_option: CorrectOption | null };

/** feat-346: the four option texts of a draft, A→D. */
const draftOptions = (d: { option_a: string; option_b: string; option_c: string; option_d: string }) => [
  d.option_a,
  d.option_b,
  d.option_c,
  d.option_d,
];

/** feat-346: patch for an option edit. For MC, taking the PICKED option from
 *  filled to blank also clears the pick (the picker offers only filled options). */
type OptionDraftShape = {
  question_type: QuestionType;
  correct_option: CorrectOption | null;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
};
function optionEditPatch<D extends OptionDraftShape>(
  draft: D,
  index: number,
  value: string,
): Partial<D> {
  const fieldKey = `option_${OPTION_LETTERS[index]}` as 'option_a';
  const out = { [fieldKey]: value } as Partial<D>;
  const correctIndex = draft.correct_option ? OPTION_LETTERS.indexOf(draft.correct_option) : null;
  if (
    draft.question_type === 'multiple_choice' &&
    blanksCorrectOption(index, correctIndex, draftOptions(draft)[index], value)
  ) {
    (out as { correct_option: CorrectOption | null }).correct_option = null;
  }
  return out;
}

/** feat-346: the inline MC option problem (gap / blank pick), in the existing
 *  `attention-*` inline-advisory skin. */
const McOptionsAdvisory: React.FC<{ message: string | null }> = ({ message }) =>
  message ? (
    <div
      role="alert"
      className="text-xs text-attention-on bg-attention-surface border border-attention-line/50 rounded p-2 mb-2"
    >
      {message}
    </div>
  ) : null;

export const QuestionRow: React.FC<{
  question: QuizQuestion;
  onSaved: () => void;
}> = ({ question, onSaved }) => {
  // feat-075: normalize the optional variants field to a string for the draft so
  // the textarea is always controlled (legacy rows arrive null/undefined).
  const [draft, patch, reset] = usePatchedState<QuestionRowDraft>({
    ...question,
    blank_accepted_variants: question.blank_accepted_variants ?? '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(
    () => reset({ ...question, blank_accepted_variants: question.blank_accepted_variants ?? '' }),
    [question, reset],
  );

  const dirty =
    JSON.stringify(draft) !==
    JSON.stringify({ ...question, blank_accepted_variants: question.blank_accepted_variants ?? '' });

  // feat-346: MC needs A + B, contiguous options and a filled pick. Fill-blank
  // is unchanged (this editor has never gated it).
  const mcProblem =
    draft.question_type === 'multiple_choice'
      ? mcOptionsProblem(draftOptions(draft), draft.correct_option)
      : null;
  const canSave = mcProblem === null && draft.correct_option !== null;

  const handleSave = async () => {
    if (!canSave || draft.correct_option === null) return;
    setSaving(true);
    try {
      const variants = (draft.blank_accepted_variants ?? '').trim();
      await updateQuestion(question.id, {
        question_type: draft.question_type,
        question_text: draft.question_text,
        option_a: draft.option_a,
        option_b: draft.option_b,
        option_c: draft.option_c,
        option_d: draft.option_d,
        correct_option: draft.correct_option,
        explanation: draft.explanation,
        correct_explanation: draft.correct_explanation,
        xp_value: draft.xp_value,
        // feat-075: fill-blank only. Send the raw textarea string (server
        // normalizes/validates); empty ⇒ null so "no variants" is a clean NULL.
        // For MC we explicitly send null so toggling away from fill-blank clears
        // any previously authored variants.
        blank_accepted_variants:
          draft.question_type === 'fill_blank' ? (variants === '' ? null : variants) : null,
        sort_order: draft.sort_order,
      });
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this question?')) return;
    await deleteQuestion(question.id);
    onSaved();
  };

  const isFill = draft.question_type === 'fill_blank';

  return (
    <div className="border rounded p-3 bg-white">
      <div className="flex items-center justify-between gap-2 mb-2">
        <QuestionTypeSelector
          value={draft.question_type}
          onChange={(question_type) => patch({ question_type })}
        />
        <XpValueField value={draft.xp_value} onChange={(xp_value) => patch({ xp_value })} />
      </div>
      {isFill && <FillBlankHint questionText={draft.question_text} />}
      <textarea
        className="border rounded px-2 py-1 text-sm w-full mb-2"
        rows={2}
        placeholder={isFill ? 'Sentence with a ___ blank' : 'Question text'}
        value={draft.question_text}
        onChange={(e) => patch({ question_text: e.target.value })}
      />
      <div className="text-xs font-medium text-gray-600 mb-1">
        {isFill ? 'Answer + MC-fallback distractors (mark the correct one)' : 'Options'}
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        {OPTION_LETTERS.map((letter, i) => {
          const fieldKey = (`option_${letter}`) as 'option_a' | 'option_b' | 'option_c' | 'option_d';
          const opts = draftOptions(draft);
          // feat-346: for MC the picker offers only FILLED options, and D is
          // locked while C is blank. Fill-blank renders exactly as before.
          const filled = isOptionFilled(opts[i]);
          const isCorrect = draft.correct_option === letter && (isFill || filled);
          const locked = !isFill && isMcOptionLocked(i, opts);
          return (
            <label
              key={letter}
              className={`flex items-start gap-2 border rounded px-2 py-1 ${
                isCorrect ? 'border-green-500 bg-green-50' : 'border-gray-200'
              }`}
            >
              <input
                type="radio"
                className="mt-1"
                checked={isCorrect}
                disabled={!isFill && !filled}
                onChange={() => patch({ correct_option: letter })}
                title={isCorrect ? 'Correct answer' : 'Mark correct'}
              />
              <span className="text-xs font-mono uppercase text-gray-500 pt-1 w-4">
                {letter}
              </span>
              <input
                className="border rounded px-2 py-1 text-sm flex-1 min-w-0"
                aria-label={`Option ${letter.toUpperCase()}`}
                placeholder={mcOptionPlaceholder(i, !isFill, opts)}
                value={draft[fieldKey]}
                disabled={locked}
                onChange={(e) => patch(optionEditPatch(draft, i, e.target.value))}
              />
            </label>
          );
        })}
      </div>
      <McOptionsAdvisory message={mcProblem?.message ?? null} />
      <div className="mb-2 text-xs text-gray-500">
        Tone: warm, specific, not cheesy. Both fields are optional; if blank, the
        drill screen falls back to whichever one is populated.
      </div>
      {isFill && (
        <AcceptedVariantsField
          value={draft.blank_accepted_variants ?? ''}
          onChange={(blank_accepted_variants) => patch({ blank_accepted_variants })}
        />
      )}
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Wrong-answer explanation
      </label>
      <textarea
        className="border rounded px-2 py-1 text-sm w-full mb-3"
        rows={2}
        placeholder="What to do differently, stated warmly. Avoid 'wrong' or 'bad'. Lead with the point."
        value={draft.explanation}
        onChange={(e) => patch({ explanation: e.target.value })}
      />
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Right-answer praise
      </label>
      <textarea
        className="border rounded px-2 py-1 text-sm w-full mb-2"
        rows={2}
        placeholder="Celebrate in one sentence. Be specific ('you named all three cheeses'), not generic ('great job'). No exclamation spam."
        value={draft.correct_explanation}
        onChange={(e) => patch({ correct_explanation: e.target.value })}
      />
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-600">Sort</label>
          <input
            type="number"
            className="border rounded px-2 py-1 text-sm w-16"
            value={draft.sort_order}
            onChange={(e) => patch({ sort_order: parseInt(e.target.value) || 0 })}
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleDelete}
            className="text-sm text-red-600 hover:text-red-800"
          >
            Delete
          </button>
          <button
            onClick={handleSave}
            disabled={!dirty || saving || !canSave}
            className="px-3 py-1 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-40"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export const NewQuestionForm: React.FC<{
  lessonId: string;
  onCreated: () => void;
  defaultSort: number;
}> = ({ lessonId, onCreated, defaultSort }) => {
  const [open, setOpen] = useState(false);
  const [draft, patch, reset] = usePatchedState({
    question_type: 'multiple_choice' as QuestionType,
    question_text: '',
    option_a: '',
    option_b: '',
    option_c: '',
    option_d: '',
    correct_option: 'a' as CorrectOption | null,
    explanation: '',
    correct_explanation: '',
    xp_value: null as number | null,
    // feat-075: newline-delimited accepted variants (fill-blank only).
    blank_accepted_variants: '',
    sort_order: defaultSort,
  });
  const [saving, setSaving] = useState(false);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="px-3 py-2 rounded border border-dashed border-gray-300 text-sm text-gray-600 hover:bg-gray-50 w-full"
      >
        + Add question
      </button>
    );
  }

  // feat-346: MC needs only A + B (C/D optional, contiguous, sent as '') and a
  // filled pick. Fill-blank still requires all four, as before.
  const isNewFill = draft.question_type === 'fill_blank';
  const newMcProblem = isNewFill
    ? null
    : mcOptionsProblem(draftOptions(draft), draft.correct_option);
  const canCreate =
    !!draft.question_text.trim() &&
    draft.correct_option !== null &&
    (isNewFill
      ? !!(draft.option_a.trim() &&
        draft.option_b.trim() &&
        draft.option_c.trim() &&
        draft.option_d.trim())
      : newMcProblem === null);

  const handleCreate = async () => {
    if (!canCreate || draft.correct_option === null) return;
    setSaving(true);
    try {
      const variants = draft.blank_accepted_variants.trim();
      await createQuestion(lessonId, {
        ...draft,
        correct_option: draft.correct_option,
        // feat-075: fill-blank only; empty ⇒ null (clean NULL = "no variants").
        blank_accepted_variants:
          draft.question_type === 'fill_blank' ? (variants === '' ? null : variants) : null,
      });
      reset({
        question_type: 'multiple_choice' as QuestionType,
        question_text: '',
        option_a: '',
        option_b: '',
        option_c: '',
        option_d: '',
        correct_option: 'a' as CorrectOption | null,
        explanation: '',
        correct_explanation: '',
        xp_value: null as number | null,
        blank_accepted_variants: '',
        sort_order: defaultSort + 1,
      });
      setOpen(false);
      onCreated();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-2 border-dashed border-blue-200 rounded p-3 bg-blue-50">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs font-medium text-blue-700">New question</div>
        <div className="flex items-center gap-2">
          <QuestionTypeSelector
            value={draft.question_type}
            onChange={(question_type) => patch({ question_type })}
          />
          <XpValueField value={draft.xp_value} onChange={(xp_value) => patch({ xp_value })} />
        </div>
      </div>
      {draft.question_type === 'fill_blank' && (
        <FillBlankHint questionText={draft.question_text} />
      )}
      <textarea
        className="border rounded px-2 py-1 text-sm w-full mb-2"
        rows={2}
        placeholder={
          draft.question_type === 'fill_blank'
            ? 'Sentence with a ___ blank'
            : 'Question text'
        }
        value={draft.question_text}
        onChange={(e) => patch({ question_text: e.target.value })}
      />
      <div className="text-xs font-medium text-gray-600 mb-1">
        {draft.question_type === 'fill_blank'
          ? 'Answer + MC-fallback distractors (mark the correct one)'
          : 'Options'}
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        {OPTION_LETTERS.map((letter, i) => {
          const fieldKey = `option_${letter}` as 'option_a' | 'option_b' | 'option_c' | 'option_d';
          const opts = draftOptions(draft);
          // feat-346: MC picker offers only filled options; D locked while C is blank.
          const filled = isOptionFilled(opts[i]);
          const isCorrect = draft.correct_option === letter && (isNewFill || filled);
          const locked = !isNewFill && isMcOptionLocked(i, opts);
          return (
            <label
              key={letter}
              className={`flex items-start gap-2 border rounded px-2 py-1 bg-white ${
                isCorrect ? 'border-green-500' : 'border-gray-200'
              }`}
            >
              <input
                type="radio"
                className="mt-1"
                checked={isCorrect}
                disabled={!isNewFill && !filled}
                onChange={() => patch({ correct_option: letter })}
              />
              <span className="text-xs font-mono uppercase text-gray-500 pt-1 w-4">
                {letter}
              </span>
              <input
                className="border rounded px-2 py-1 text-sm flex-1 min-w-0"
                aria-label={`Option ${letter.toUpperCase()}`}
                placeholder={mcOptionPlaceholder(i, !isNewFill, opts)}
                value={draft[fieldKey]}
                disabled={locked}
                onChange={(e) => patch(optionEditPatch(draft, i, e.target.value))}
              />
            </label>
          );
        })}
      </div>
      {/* feat-346: "A and B are required" is what the disabled Create already
          says on a blank form, so only the gap / blank-pick problems speak. */}
      <McOptionsAdvisory
        message={newMcProblem && newMcProblem.code !== 'too_few' ? newMcProblem.message : null}
      />
      <div className="mb-1 text-xs text-blue-700/80">
        Tone: warm, specific, not cheesy.
      </div>
      {draft.question_type === 'fill_blank' && (
        <AcceptedVariantsField
          value={draft.blank_accepted_variants}
          onChange={(blank_accepted_variants) => patch({ blank_accepted_variants })}
        />
      )}
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Wrong-answer explanation (optional)
      </label>
      <textarea
        className="border rounded px-2 py-1 text-sm w-full mb-2"
        rows={2}
        placeholder="What to do differently, stated warmly."
        value={draft.explanation}
        onChange={(e) => patch({ explanation: e.target.value })}
      />
      <label className="block text-xs font-medium text-gray-600 mb-1">
        Right-answer praise (optional)
      </label>
      <textarea
        className="border rounded px-2 py-1 text-sm w-full mb-2"
        rows={2}
        placeholder="Celebrate in one sentence. Be specific, not generic."
        value={draft.correct_explanation}
        onChange={(e) => patch({ correct_explanation: e.target.value })}
      />
      <div className="flex gap-2 justify-end">
        <button
          onClick={() => setOpen(false)}
          className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
        <button
          onClick={handleCreate}
          disabled={!canCreate || saving}
          className="px-3 py-1 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-40"
        >
          {saving ? 'Creating…' : 'Create question'}
        </button>
      </div>
    </div>
  );
};

/**
 * feat-251 AC21 — the rehearsal-role list, with a LOAD-FAILURE sentinel.
 *
 * `undefined` means the `/rehearsal-roles` fetch FAILED. `[]` means it
 * succeeded and this tenant genuinely has no role with an active prompt
 * template version. Those are different facts about the world — the second is
 * a real, valid state a user can fix in Prompt Management, the first is a bug —
 * and the rehearsal editor must not render one as the other. Before feat-251
 * the fetch was `.catch(() => [])`, so a 403 (which is what every store manager
 * got, because the old endpoint was corporate-only) presented as the confident
 * claim "This tenant has no active prompt template versions."
 *
 * Same "never loaded vs loaded and empty" sentinel the tracks fetch in this
 * file already uses (feat-235) — `tracks === undefined` there, for the same
 * reason: never assert a fact from ignorance.
 */
type AvailableRoles = string[] | undefined;

// ============================================================
// Lesson block
// ============================================================

const LessonBlock: React.FC<{
  lesson: QuizLesson;
  tenantId: string;
  availableRoles: AvailableRoles;
  onChanged: () => void;
}> = ({ lesson, tenantId, availableRoles, onChanged }) => {
  const [expanded, setExpanded] = useState(false);
  const [meta, patchMeta, resetMeta] = usePatchedState<{
    title: string;
    emoji: string;
    icon_key: string | null;
    xp_reward: number;
    question_count: number | null;
    sort_order: number;
    active: boolean;
  }>({
    title: lesson.title,
    emoji: lesson.emoji,
    icon_key: lesson.icon_key,
    xp_reward: lesson.xp_reward,
    question_count: lesson.question_count,
    sort_order: lesson.sort_order,
    active: lesson.active,
  });
  const [savingMeta, setSavingMeta] = useState(false);

  useEffect(() => {
    resetMeta({
      title: lesson.title,
      emoji: lesson.emoji,
      icon_key: lesson.icon_key,
      xp_reward: lesson.xp_reward,
      question_count: lesson.question_count,
      sort_order: lesson.sort_order,
      active: lesson.active,
    });
  }, [lesson, resetMeta]);

  const dirty =
    meta.title !== lesson.title ||
    meta.emoji !== lesson.emoji ||
    meta.icon_key !== lesson.icon_key ||
    meta.xp_reward !== lesson.xp_reward ||
    meta.question_count !== lesson.question_count ||
    meta.sort_order !== lesson.sort_order ||
    meta.active !== lesson.active;

  const handleSaveMeta = async () => {
    setSavingMeta(true);
    try {
      await updateLesson(lesson.id, meta);
      onChanged();
    } finally {
      setSavingMeta(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete lesson "${lesson.title}" and all its questions?`)) return;
    await deleteLesson(lesson.id);
    onChanged();
  };

  return (
    <div className="border rounded-lg bg-gray-50">
      <div className="p-3 space-y-2">
      <div className="flex items-center gap-2">
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-gray-500 hover:text-gray-800 text-xs w-4"
          title="Expand"
        >
          {expanded ? '▼' : '▶'}
        </button>
        <LessonIconIndicator
          iconKey={meta.icon_key}
          emoji={meta.emoji}
          onClick={() => setExpanded(true)}
        />
        <input
          className="border rounded px-2 py-1 text-sm flex-1"
          placeholder="Lesson title"
          value={meta.title}
          onChange={(e) => patchMeta({ title: e.target.value })}
        />
        <label className="text-xs text-gray-600 flex items-center gap-1">
          XP
          <input
            type="number"
            className="border rounded px-2 py-1 text-sm w-16"
            value={meta.xp_reward}
            onChange={(e) => patchMeta({ xp_reward: parseInt(e.target.value) || 0 })}
          />
        </label>
        <label
          className="text-xs text-gray-600 flex items-center gap-1"
          title="Questions shown per session. Leave blank to show all."
        >
          Qs/session
          <input
            type="number"
            min={1}
            max={100}
            placeholder="All"
            className="border rounded px-2 py-1 text-sm w-16"
            value={meta.question_count ?? ''}
            onChange={(e) => {
              const v = e.target.value.trim();
              if (v === '') {
                patchMeta({ question_count: null });
              } else {
                const n = parseInt(v, 10);
                patchMeta({ question_count: Number.isFinite(n) ? n : null });
              }
            }}
          />
        </label>
        <label className="text-xs text-gray-600 flex items-center gap-1">
          Sort
          <input
            type="number"
            className="border rounded px-2 py-1 text-sm w-14"
            value={meta.sort_order}
            onChange={(e) => patchMeta({ sort_order: parseInt(e.target.value) || 0 })}
          />
        </label>
        <label className="text-xs text-gray-600 flex items-center gap-1">
          <input
            type="checkbox"
            checked={meta.active}
            onChange={(e) => patchMeta({ active: e.target.checked })}
          />
          Active
        </label>
        <button
          onClick={handleSaveMeta}
          disabled={!dirty || savingMeta}
          className="px-2 py-1 rounded bg-blue-600 text-white text-xs hover:bg-blue-700 disabled:opacity-40"
        >
          {savingMeta ? 'Saving…' : 'Save'}
        </button>
        <button
          onClick={handleDelete}
          className="px-2 py-1 text-xs text-red-600 hover:text-red-800"
        >
          Delete
        </button>
      </div>
        {expanded && (
          <IconKeyPicker
            iconKey={meta.icon_key}
            emoji={meta.emoji}
            onIconKey={(icon_key) => patchMeta({ icon_key })}
            onEmoji={(emoji) => patchMeta({ emoji })}
          />
        )}
      </div>

      {expanded && (
        <LessonBody
          lesson={lesson}
          tenantId={tenantId}
          availableRoles={availableRoles}
          onChanged={onChanged}
        />
      )}
    </div>
  );
};

// ============================================================
// Lesson body — interleaved question + rehearsal item list
// ============================================================

export type LessonItem =
  | { kind: 'question'; q: QuizQuestion }
  | { kind: 'rehearsal'; r: RehearsalLessonItem }
  // feat-165: explanatory video item (interleaves at its own sort_order).
  | { kind: 'video'; v: VideoItemSummary }
  // feat-166: reading item (interleaves at its own sort_order).
  | { kind: 'reading'; rd: ReadingItemSummary };

function itemSort(it: LessonItem): number {
  return it.kind === 'question'
    ? it.q.sort_order
    : it.kind === 'rehearsal'
      ? it.r.sort_order
      : it.kind === 'video'
        ? it.v.sort_order
        : it.rd.sort_order;
}
function itemId(it: LessonItem): string {
  return it.kind === 'question'
    ? it.q.id
    : it.kind === 'rehearsal'
      ? it.r.id
      : it.kind === 'video'
        ? it.v.id
        : it.rd.id;
}
// Deterministic tie-break at an equal sort_order: questions → rehearsals →
// videos → readings. Matches the backend `get_quiz_lesson` mobile tie-break
// (`_type_rank` questions=0, rehearsals=1, videos=2, readings=3) so the editor
// order agrees with what servers see on mobile (ADR 0001 D4/D6).
function itemTypeRank(it: LessonItem): number {
  return it.kind === 'question' ? 0 : it.kind === 'rehearsal' ? 1 : it.kind === 'video' ? 2 : 3;
}

export function interleaveItems(lesson: QuizLesson): LessonItem[] {
  const items: LessonItem[] = [];
  for (const q of lesson.question_items) items.push({ kind: 'question', q });
  for (const r of lesson.rehearsal_items) items.push({ kind: 'rehearsal', r });
  // Older backends omit video_items; treat undefined as none.
  for (const v of lesson.video_items ?? []) items.push({ kind: 'video', v });
  // feat-166: older backends omit reading_items; treat undefined as none.
  for (const rd of lesson.reading_items ?? []) items.push({ kind: 'reading', rd });
  items.sort((a, b) => {
    const sa = itemSort(a);
    const sb = itemSort(b);
    if (sa !== sb) return sa - sb;
    const ka = itemTypeRank(a);
    const kb = itemTypeRank(b);
    if (ka !== kb) return ka - kb;
    return itemId(a).localeCompare(itemId(b));
  });
  return items;
}

// ---------- Inline criterion editor (one row) ----------

const RehearsalCriterionEditor: React.FC<{
  criterion: RehearsalCriterion;
  isFirst: boolean;
  isLast: boolean;
  onChange: (next: RehearsalCriterion) => void;
  /** feat-259 AC14 — a criterion's text fields auto-save on blur like any other
   *  field. The whole `criteria` list is ONE field in the diff, so this commits
   *  that field rather than anything per-row. */
  onCommit: () => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
}> = ({ criterion, isFirst, isLast, onChange, onCommit, onRemove, onMoveUp, onMoveDown }) => {
  const tone =
    criterion.kind === 'good'
      ? 'border-green-300 bg-green-50'
      : 'border-red-300 bg-red-50';
  return (
    <div className={`border rounded p-2 ${tone}`}>
      <div className="flex items-center gap-2 mb-1">
        <input
          className="border rounded px-2 py-1 text-xs flex-1"
          placeholder={
            criterion.kind === 'good'
              ? 'Good-thing label (chip text on result screen)'
              : 'Bad-thing label'
          }
          value={criterion.label}
          onChange={(e) => onChange({ ...criterion, label: e.target.value })}
          onBlur={onCommit}
        />
        <button
          onClick={onMoveUp}
          disabled={isFirst}
          className="px-1 text-xs text-gray-600 disabled:opacity-30"
          title="Move up"
        >
          ↑
        </button>
        <button
          onClick={onMoveDown}
          disabled={isLast}
          className="px-1 text-xs text-gray-600 disabled:opacity-30"
          title="Move down"
        >
          ↓
        </button>
        <button
          onClick={onRemove}
          className="px-1 text-xs text-red-600 hover:text-red-800"
          title="Remove"
        >
          ×
        </button>
      </div>
      <textarea
        className="border rounded px-2 py-1 text-xs w-full mb-1"
        rows={2}
        placeholder="Description sent to the AI to decide hit/miss"
        value={criterion.description}
        onChange={(e) =>
          onChange({ ...criterion, description: e.target.value })
        }
        onBlur={onCommit}
      />
      <textarea
        className="border rounded px-2 py-1 text-xs w-full text-gray-700"
        rows={2}
        placeholder="Optional example phrases (one per line) — used as anchors"
        value={criterion.example_phrases ?? ''}
        onChange={(e) =>
          onChange({ ...criterion, example_phrases: e.target.value })
        }
        onBlur={onCommit}
      />
    </div>
  );
};

function makeCriterion(
  kind: 'good' | 'bad',
  position: number,
): RehearsalCriterion {
  return {
    kind,
    label: '',
    description: '',
    example_phrases: '',
    position,
  };
}

export interface RehearsalDraft {
  name: string;
  role: string;
  scenario_text: string;
  scenario_image_s3_key: string | null;
  /** feat-033: rehearsal-screen banner label (e.g. "Drive-thru, lunch rush"). */
  location_label: string;
  /** feat-033: model answer shown in the "Try saying" card on AI-graded fail. */
  correct_example_response: string;
  xp_value: number;
  redo_xp: number;
  fail_xp: number;
  max_recording_seconds: number;
  sort_order: number;
  criteria: RehearsalCriterion[];
}

/** One source for each field's on-screen label — the `<label>` the editor reads
 *  and the name the error banner uses are the same string by construction. */
export const REHEARSAL_FIELD_LABELS: Record<keyof RehearsalDraft, string> = {
  name: 'Name',
  role: 'Role',
  scenario_text: 'Scenario text',
  scenario_image_s3_key: 'Scenario image',
  location_label: 'Location label',
  correct_example_response: 'Correct example response',
  xp_value: 'XP value',
  redo_xp: 'Redo XP',
  fail_xp: 'Fail XP',
  max_recording_seconds: 'Max recording seconds',
  sort_order: 'Sort order',
  // design/error-states.html state C names criteria positionally, because a
  // criterion's own label may be the empty string that failed to save. The diff
  // carries the criteria list as ONE field, so this is the label the banner
  // falls back to; `rehearsalErrorLabels` names the individual criteria by group
  // and position ("Good thing 2 label") whenever the two lists are comparable.
  criteria: 'Criteria',
};

export const REHEARSAL_COPY = {
  /** design/NOTES.md §6a — the `role` case is a CREATE-BLOCKER, not a
   *  field-level bound, so it takes state G's lede shape ("Nothing has been
   *  saved yet") and NOT state E's ("lower it and it saves itself"). State E is
   *  scoped to one held field on an item that EXISTS; here nothing can be
   *  created at all, so every field is held. */
  rolePick: 'Pick a role before this rehearsal can be created.',
  /** …and when the tenant has no active prompt template version to pick, the
   *  editor cannot fix it from this panel at all. Same blocker, different family
   *  — `danger-*`, because `attention-*` promises a fix that is available here. */
  roleUnavailable:
    "This rehearsal can't be created until at least one role has an active prompt template version.",
  uploadInFlight: 'The scenario image is still uploading.',
  /** Author-fixable, so `attention-*` and no Retry — the value is never sent.
   *  Mirrors the backend cross-field check (HTTP 400, ADR D2/D4). */
  redoAboveXp: (redo: number, xp: number) =>
    `Redo XP can't be more than XP. ${redo} is above ${xp}, so this one isn't being saved. Lower it and it saves itself.`,
  failAboveXp: (fail: number, xp: number) =>
    `Fail XP can't be more than XP. ${fail} is above ${xp}, so this one isn't being saved. Lower it and it saves itself.`,
  xpOutOfRange: 'XP must be a whole number between 0 and 1000.',
  secondsOutOfRange: 'Max recording seconds must be a whole number between 5 and 600.',
} as const;

const XP_MAX = 1000;
const RECORDING_SECONDS_MIN = 5;
const RECORDING_SECONDS_MAX = 600;

/**
 * feat-259 / brief §6.3 — the XP-range rules are REAL, not advisories. The
 * backend enforces `ge`/`le` and a cross-field 400, so an out-of-range value
 * would 422/400. It is held back rather than sent: the app already knows the
 * answer, and firing a doomed request so a generic banner can offer a Try again
 * that cannot work is worse than not firing it (design/error-states.html state
 * E). Only the offending field is held; the rest of the same save goes out.
 */
export function rehearsalFieldErrors(
  fields: (keyof RehearsalDraft & string)[],
  d: RehearsalDraft,
): Array<{ field: keyof RehearsalDraft & string; message: string }> {
  const out: Array<{ field: keyof RehearsalDraft & string; message: string }> = [];
  const bad = (n: number, max: number) => !Number.isInteger(n) || n < 0 || n > max;
  if (fields.includes('xp_value') && bad(d.xp_value, XP_MAX)) {
    out.push({ field: 'xp_value', message: REHEARSAL_COPY.xpOutOfRange });
  }
  if (fields.includes('redo_xp') && (bad(d.redo_xp, XP_MAX) || d.redo_xp > d.xp_value)) {
    out.push({ field: 'redo_xp', message: REHEARSAL_COPY.redoAboveXp(d.redo_xp, d.xp_value) });
  }
  if (fields.includes('fail_xp') && (bad(d.fail_xp, XP_MAX) || d.fail_xp > d.xp_value)) {
    out.push({ field: 'fail_xp', message: REHEARSAL_COPY.failAboveXp(d.fail_xp, d.xp_value) });
  }
  if (
    fields.includes('max_recording_seconds') &&
    (!Number.isInteger(d.max_recording_seconds) ||
      d.max_recording_seconds < RECORDING_SECONDS_MIN ||
      d.max_recording_seconds > RECORDING_SECONDS_MAX)
  ) {
    out.push({ field: 'max_recording_seconds', message: REHEARSAL_COPY.secondsOutOfRange });
  }
  return out;
}

/**
 * feat-259 / design/NOTES.md §6b — name a failed criterion the way the EDITOR
 * sees it: `Good thing 2 label`, `Bad thing 1 description`.
 *
 * NOT a flat `draft.criteria` index. `RehearsalCriterionEditor` renders no
 * number at all, and the two kinds are rendered as two separately-`filter`ed and
 * separately-indexed lists ("Good things (what to hear)" / "Bad things (to
 * avoid)"). The flat array interleaves them, so flat #2 can be the FIRST row of
 * the red group — the banner would name a position the editor cannot see. Group
 * + 1-based position within that group is the only identifier on screen, and it
 * matches the placeholder copy those inputs already carry. The criterion's own
 * label cannot serve: it may be the empty string that failed to save.
 */
export function rehearsalErrorLabels(
  fields: (keyof RehearsalDraft & string)[],
  draft: RehearsalDraft,
  baseline: RehearsalDraft,
): string[] {
  const out: string[] = [];
  fields.forEach((f) => {
    if (f !== 'criteria') {
      out.push(REHEARSAL_FIELD_LABELS[f]);
      return;
    }
    // Positions only line up while the two lists are the same length. An add,
    // a remove or a reorder makes an index-to-index comparison meaningless, so
    // fall back to the list's own label rather than name a wrong position.
    const comparable = draft.criteria.length === baseline.criteria.length;
    const named: string[] = [];
    if (comparable) {
      const seen: Record<string, number> = { good: 0, bad: 0 };
      draft.criteria.forEach((c, i) => {
        const before = baseline.criteria[i];
        seen[c.kind] += 1;
        const where = `${c.kind === 'good' ? 'Good thing' : 'Bad thing'} ${seen[c.kind]}`;
        if (!before || before.kind !== c.kind) return; // the row is not the same row
        if (before.label !== c.label) named.push(`${where} label`);
        if (before.description !== c.description) named.push(`${where} description`);
        if ((before.example_phrases ?? '') !== (c.example_phrases ?? '')) {
          named.push(`${where} example phrases`);
        }
      });
    }
    out.push(...(named.length ? named : [REHEARSAL_FIELD_LABELS.criteria]));
  });
  return out;
}

function emptyRehearsalDraft(
  defaultSort: number,
  availableRoles: string[],
): RehearsalDraft {
  return {
    name: '',
    role: availableRoles[0] ?? '',
    scenario_text: '',
    scenario_image_s3_key: null,
    location_label: '',
    correct_example_response: '',
    xp_value: 25,
    redo_xp: 0,
    fail_xp: 0,
    max_recording_seconds: 60,
    sort_order: defaultSort,
    criteria: [],
  };
}

// ---------- Inline editor (shared by create + edit) ----------

export const RehearsalEditor: React.FC<{
  tenantId: string;
  initial: RehearsalDraft;
  existingId: string | null; // null = create mode
  pinnedVersionNumber: number | null;
  availableRoles: AvailableRoles;
  /** Fired ONCE, when the first blur creates the row (AC10 — a create still
   *  reloads; an auto-save does not). */
  onCreated: (savedId: string) => void;
  onDelete?: () => void;
  lessonId: string;
  /** feat-252 — read-only server timestamps for the metadata line. Deliberately
   *  SEPARATE props rather than fields on `RehearsalDraft`.
   *
   *  The reason is NOT that the draft is spread into a request body — it is not.
   *  `handleSave` enumerates the `createRehearsal` / `updateRehearsal` bodies
   *  field by field, and every `...draft` in this editor is a
   *  `setDraft({ ...draft, … })` local-state update. Adding these two fields to
   *  `RehearsalDraft` would leak nothing today; that was verified by mutation,
   *  and the payload test stayed green.
   *
   *  The real reason is the more useful one: the invariant "no request body
   *  carries `created`/`updated`" holds by ENUMERATION DISCIPLINE at every write
   *  path in these files, not by any draft type's shape. A draft means EDITABLE
   *  FIELDS ONLY. The moment it also means "and some read-only server facts",
   *  that hand-maintained enumeration is the only thing standing between those
   *  facts and a request body — and it is one refactor from not being. Keeping
   *  them out of the type is what makes the leak unrepresentable rather than
   *  merely unperformed. */
  created?: string;
  updated?: string;
  /** feat-259 — the shell's flush registration and AT9 hold. */
  registerFlush?: PanelSaveWiring['registerFlush'];
  pendingNavigation?: boolean;
  onNavigationProceed?: PanelSaveWiring['onNavigationProceed'];
  onPatched?: PanelSaveWiring['onPatched'];
}> = ({
  tenantId,
  initial,
  existingId,
  pinnedVersionNumber,
  availableRoles,
  onCreated,
  onDelete,
  lessonId,
  created,
  updated,
  registerFlush,
  pendingNavigation = false,
  onNavigationProceed,
  onPatched,
}) => {
  const [uploading, setUploading] = useState(false);
  const uploadingRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  // feat-251 AC21 — see `AvailableRoles`. A failed roles fetch and an empty
  // roles list both leave nothing to pick, but they are DIFFERENT facts and
  // this form says so: one is a bug to report, the other is a setup step the
  // user can go and do.
  const rolesLoadFailed = availableRoles === undefined;
  const roleOptions = availableRoles ?? [];

  // feat-259: no `useEffect(() => setDraft(initial), [initial])`. Under
  // auto-save its only remaining job would be to overwrite the editor's live
  // draft from a prop; selection changes remount this editor via its `key`.
  const save = useAutoSave<RehearsalDraft>({
    initial,
    itemId: existingId,
    labels: REHEARSAL_FIELD_LABELS,
    create: async (d) => {
      const { id } = await createRehearsal(lessonId, {
        role: d.role,
        name: d.name,
        scenario_text: d.scenario_text,
        scenario_image_s3_key: d.scenario_image_s3_key,
        location_label: d.location_label,
        correct_example_response: d.correct_example_response,
        xp_value: d.xp_value,
        redo_xp: d.redo_xp,
        fail_xp: d.fail_xp,
        max_recording_seconds: d.max_recording_seconds,
        sort_order: d.sort_order,
        criteria: d.criteria,
      });
      return id;
    },
    // `role` is create-only (locked in edit), so it can never be in a diff.
    update: (id, changed) => updateRehearsal(id, changed as Parameters<typeof updateRehearsal>[1]),
    // brief §6.3 — `noRoles` is a GENUINE CREATE GATE, not an advisory. The
    // backend resolves the NOT NULL FK `prompt_template_version_id` from
    // `role`, so a create without one cannot succeed; everything else in the
    // old `blocked` condition (missing name / scenario / half-filled criterion)
    // becomes advisory and saves anyway. It gates only the CREATE — an existing
    // rehearsal keeps auto-saving whatever its role is.
    createGate: (d) => {
      if (d.role.trim() !== '') return null;
      return roleOptions.length === 0 ? REHEARSAL_COPY.roleUnavailable : REHEARSAL_COPY.rolePick;
    },
    holdWrites: () => (uploadingRef.current ? REHEARSAL_COPY.uploadInFlight : null),
    validateFields: (fields, d) => rehearsalFieldErrors(fields, d),
    describeFields: (fields, d, base) => rehearsalErrorLabels(fields, d, base),
    onPersisted: ({ id, changed, created: wasCreate }) => {
      if (wasCreate) onCreated(id);
      else onPatched?.(id, changed as Record<string, unknown>);
    },
  });
  const draft = save.draft;
  const setDraft = (next: RehearsalDraft) => save.setFields(next);

  useEffect(() => {
    registerFlush?.(save.flush);
    return () => registerFlush?.(null);
  }, [registerFlush, save.flush]);

  useEffect(() => {
    uploadingRef.current = uploading;
    if (!uploading && save.isDirty) void save.flush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploading]);

  const handleNavRetry = async () => {
    if (await save.flush()) onNavigationProceed?.();
  };

  // Typing inside a criterion is a TEXT edit: local only, committed on blur by
  // `RehearsalCriterionEditor`'s `onCommit` (AC14).
  const updateCriterion = (idx: number, next: RehearsalCriterion) =>
    save.setField(
      'criteria',
      draft.criteria.map((c, i) => (i === idx ? next : c)),
    );

  // Add / remove / reorder are non-text controls: they write on change (AC2).
  const removeCriterion = (idx: number) =>
    save.saveField(
      'criteria',
      draft.criteria.filter((_, i) => i !== idx).map((c, i) => ({ ...c, position: i })),
    );

  const moveCriterion = (idx: number, delta: number) => {
    const next = draft.criteria.map((c) => ({ ...c }));
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    next.forEach((c, i) => (c.position = i));
    save.saveField('criteria', next);
  };

  const addCriterion = (kind: 'good' | 'bad') =>
    save.saveField('criteria', [...draft.criteria, makeCriterion(kind, draft.criteria.length)]);

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadingRef.current = true;
    setUploading(true);
    setError(null);
    try {
      const { s3_key } = await uploadScenarioImage(file, tenantId);
      // A non-text control: it writes on change (AC2).
      save.saveField('scenario_image_s3_key', s3_key);
    } catch (err) {
      setError(String(err));
    } finally {
      uploadingRef.current = false;
      setUploading(false);
    }
  };

  /** Text/number field: edit locally, write on blur. */
  const commit = (field: keyof RehearsalDraft) => () => save.commitField(field as string & keyof RehearsalDraft);

  /** `location_label` and `correct_example_response` are nullable on the wire —
   *  the old explicit Save sent `.trim() || null`. Settle a blank value back to
   *  `''`-vs-`null`'s canonical form ON BLUR so the field can reach a clean
   *  state, instead of re-sending on every later blur. */
  const commitNullable = (field: 'location_label' | 'correct_example_response') => () => {
    const trimmed = draft[field].trim();
    if (trimmed !== draft[field]) save.setField(field, trimmed);
    save.commitField(field);
  };

  const handleDelete = () => onDelete?.();

  return (
    <div className="border-l-4 border-purple-400 bg-purple-50 rounded p-3 space-y-3">
      {/* feat-270 panels 4 / 9 — ONE panel-level block, never one per field. */}
      <PanelApprovalState />
      {/* feat-252: the header row and the timestamp are wrapped together so they
          are ONE `space-y-3` child — the timestamp is therefore not a `space-y`
          sibling and its own `mt-0.5` governs the gap. */}
      <div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">
            🎙️ Rehearsal
          </span>
          {existingId !== null && pinnedVersionNumber != null && (
            <span className="text-xs text-purple-700">
              Pinned prompt v{pinnedVersionNumber}
            </span>
          )}
          <span className="ml-auto" />
          {/* feat-078: Test harness — saved-only. New/unsaved rehearsals show a
              disabled button with a "Save first" hint (it grades the saved
              version, AC1). Opens in a SEPARATE window so the author can edit
              curriculum and run the harness side-by-side. */}
          {existingId === null ? (
            <button
              type="button"
              disabled
              title="Save first to test"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-gray-200 text-gray-400 cursor-not-allowed"
            >
              <Play className="w-3.5 h-3.5" />
              Test
            </button>
          ) : (
            <button
              type="button"
              onClick={() => openTestHarnessWindow(existingId, draft.name, pinnedVersionNumber)}
              title="Open the test harness in a separate window"
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded border border-purple-300 text-purple-700 hover:bg-purple-100"
            >
              <Play className="w-3.5 h-3.5" />
              Test
            </button>
          )}
        </div>
        <ContentTimestamps variant="stacked" className="mt-0.5" created={created} updated={updated} />
      </div>

      {error && (
        <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-800">
          {error}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 text-xs">
        <label className="flex flex-col gap-1">
          Name
          <input
            className="border rounded px-2 py-1 text-sm"
            value={draft.name}
            onChange={(e) => save.setField('name', e.target.value)}
            onBlur={commit('name')}
          />
        </label>
        <label className="flex flex-col gap-1">
          Role {existingId !== null && <span className="text-gray-500">(locked)</span>}
          {existingId !== null ? (
            <input
              className="border rounded px-2 py-1 text-sm"
              value={draft.role}
              disabled
            />
          ) : (
            <select
              className="border rounded px-2 py-1 text-sm"
              value={draft.role}
              // AC2 names the rehearsal role dropdown as a non-text control, so
              // it writes on CHANGE. In create mode that write IS the create —
              // which is consistent with spec §Creating a new item ("the row is
              // created as soon as focus leaves the first field the editor
              // touched, even if that field is empty"). It can only fire here:
              // in edit mode the role is locked, because the backend pins
              // `prompt_template_version_id` from it at create time.
              onChange={(e) => save.saveField('role', e.target.value)}
              disabled={roleOptions.length === 0}
            >
              {roleOptions.length === 0 && (
                <option value="">
                  {rolesLoadFailed
                    ? '— roles unavailable —'
                    : '— no active prompt versions —'}
                </option>
              )}
              {roleOptions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          )}
        </label>
        <label className="flex flex-col gap-1">
          XP value (first-pass)
          <input
            type="number"
            min={0}
            max={1000}
            className="border rounded px-2 py-1 text-sm"
            value={draft.xp_value}
            onChange={(e) => save.setField('xp_value', parseInt(e.target.value) || 0)}
            onBlur={commit('xp_value')}
          />
        </label>
        <label className="flex flex-col gap-1">
          Redo XP
          <input
            type="number"
            min={0}
            max={1000}
            className="border rounded px-2 py-1 text-sm"
            value={draft.redo_xp}
            onChange={(e) => save.setField('redo_xp', parseInt(e.target.value) || 0)}
            onBlur={commit('redo_xp')}
          />
          <span className="text-[10px] text-gray-500">
            XP awarded on every passing attempt after the first. 0 ⇒ redo
            passes award nothing.
          </span>
        </label>
        <label className="flex flex-col gap-1">
          Fail XP
          <input
            type="number"
            min={0}
            max={1000}
            className="border rounded px-2 py-1 text-sm"
            value={draft.fail_xp}
            onChange={(e) => save.setField('fail_xp', parseInt(e.target.value) || 0)}
            onBlur={commit('fail_xp')}
          />
          <span className="text-[10px] text-gray-500">
            Consolation XP awarded when an attempt fails (passed=false).
            0 ⇒ failed attempts award nothing.
          </span>
        </label>
        <label className="flex flex-col gap-1">
          Max recording seconds
          <input
            type="number"
            min={5}
            max={600}
            className="border rounded px-2 py-1 text-sm"
            value={draft.max_recording_seconds}
            onChange={(e) => save.setField('max_recording_seconds', parseInt(e.target.value) || 60)}
            onBlur={commit('max_recording_seconds')}
          />
        </label>
        <label className="flex flex-col gap-1">
          Sort order
          <input
            type="number"
            className="border rounded px-2 py-1 text-sm w-24"
            value={draft.sort_order}
            onChange={(e) => save.setField('sort_order', parseInt(e.target.value) || 0)}
            onBlur={commit('sort_order')}
          />
        </label>
      </div>

      <label className="text-xs flex flex-col gap-1">
        <span>
          Location label (banner shown on the rehearsal screen, e.g. "Drive-thru,
          lunch rush")
          <ApprovedFieldMarker field="location_label" />
        </span>
        <input
          className="border rounded px-2 py-1 text-sm w-full"
          placeholder="Optional setting/location for the scene"
          value={draft.location_label}
          onChange={(e) => save.setField('location_label', e.target.value)}
          onBlur={commitNullable('location_label')}
        />
        <ApprovedValueLine field="location_label" />
      </label>

      <label className="text-xs flex flex-col gap-1">
        <span>
          Scenario text (shown to the server before they start)
          <ApprovedFieldMarker field="scenario_text" />
        </span>
        <textarea
          rows={4}
          className="border rounded px-2 py-1 text-sm w-full"
          value={draft.scenario_text}
          onChange={(e) => save.setField('scenario_text', e.target.value)}
          onBlur={commit('scenario_text')}
        />
        <ApprovedValueLine field="scenario_text" />
      </label>

      <label className="text-xs flex flex-col gap-1">
        <span>
          Correct example response (model answer shown in the "Try saying" card
          when the learner's spoken attempt fails grading)
          <ApprovedFieldMarker field="correct_example_response" />
        </span>
        <textarea
          rows={3}
          className="border rounded px-2 py-1 text-sm w-full"
          placeholder="A strong example of what the server could say"
          value={draft.correct_example_response}
          onChange={(e) => save.setField('correct_example_response', e.target.value)}
          onBlur={commitNullable('correct_example_response')}
        />
        <ApprovedValueLine field="correct_example_response" />
      </label>

      <div className="text-xs">
        Optional scenario image
        <div className="flex items-center gap-3 mt-1">
          <input
            type="file"
            accept="image/*"
            onChange={handleImageSelect}
            disabled={uploading}
          />
          {uploading && <span className="text-xs">Uploading…</span>}
          {draft.scenario_image_s3_key && (
            <span className="text-xs text-gray-700 truncate max-w-xs">
              ✓ {draft.scenario_image_s3_key.split('/').pop()}
            </span>
          )}
        </div>
      </div>

      <div className="border-t pt-2">
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-xs font-semibold">Good things (what to hear)</h4>
          <button
            onClick={() => addCriterion('good')}
            className="px-2 py-1 text-xs rounded border border-green-500 text-green-700 hover:bg-green-50"
          >
            + Good thing
          </button>
        </div>
        <div className="space-y-1">
          {draft.criteria
            .filter((c) => c.kind === 'good')
            .map((c, i, arr) => {
              const flatIdx = draft.criteria.indexOf(c);
              return (
                <RehearsalCriterionEditor
                  key={`good-${flatIdx}`}
                  criterion={c}
                  isFirst={i === 0}
                  isLast={i === arr.length - 1}
                  onChange={(next) => updateCriterion(flatIdx, next)}
                  onCommit={() => save.commitField('criteria')}
                  onRemove={() => removeCriterion(flatIdx)}
                  onMoveUp={() => moveCriterion(flatIdx, -1)}
                  onMoveDown={() => moveCriterion(flatIdx, 1)}
                />
              );
            })}
        </div>
      </div>

      <div className="border-t pt-2">
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-xs font-semibold">Bad things (to avoid)</h4>
          <button
            onClick={() => addCriterion('bad')}
            className="px-2 py-1 text-xs rounded border border-red-500 text-red-700 hover:bg-red-50"
          >
            + Bad thing
          </button>
        </div>
        <div className="space-y-1">
          {draft.criteria
            .filter((c) => c.kind === 'bad')
            .map((c, i, arr) => {
              const flatIdx = draft.criteria.indexOf(c);
              return (
                <RehearsalCriterionEditor
                  key={`bad-${flatIdx}`}
                  criterion={c}
                  isFirst={i === 0}
                  isLast={i === arr.length - 1}
                  onChange={(next) => updateCriterion(flatIdx, next)}
                  onCommit={() => save.commitField('criteria')}
                  onRemove={() => removeCriterion(flatIdx)}
                  onMoveUp={() => moveCriterion(flatIdx, -1)}
                  onMoveDown={() => moveCriterion(flatIdx, 1)}
                />
              );
            })}
        </div>
      </div>

      {/*
        feat-270 AT-26b / NOTES §10.12 — the criteria rubric, as ONE gated field.

        The marker and the value line come from the SERVER's `fields[].changed`
        on `criteria`, exactly like every other field. There is deliberately NO
        comparison of the two lists' LENGTHS here, and no separate "a criterion
        was removed" treatment: removing a criterion is an EDIT of the rehearsal,
        the API marks it `change_kind: "updated"`, and learners keep being graded
        against the approved rubric until the Lesson is approved. The removal
        panel's "already gone" would be false, in the direction that makes an
        approver relax.

        The approved value renders as a numbered SEQUENCE (ADR-A §5.1: order is
        part of the rubric's meaning, so reordering criteria un-approves), which
        `ApprovedValueLine` does for any array value.
      */}
      <div className="text-xs">
        <span className="sr-only">Criteria</span>
        <ApprovedFieldMarker field="criteria" />
        <ApprovedValueLine field="criteria" />
      </div>

      {(() => {
        const partial = draft.criteria.some(
          (c) => !c.label.trim() || !c.description.trim(),
        );
        const nameMissing = !draft.name.trim();
        const scenarioMissing = !draft.scenario_text.trim();
        const roleMissing = !draft.role.trim();
        const blockedReasons: string[] = [];
        if (nameMissing) blockedReasons.push('name');
        if (scenarioMissing) blockedReasons.push('scenario text');
        // feat-259 / design/NOTES.md §6c — `role` is deliberately NOT in this
        // list any more. Name, scenario text and a half-filled criterion are
        // ADVISORY: the item saves perfectly well without them. `role` is a hard
        // create gate — the backend resolves the NOT NULL FK
        // `prompt_template_version_id` from it — and it is handled as one in the
        // save controller. Listing them together told the editor they were the
        // same kind of thing, and that is what made the create-gate banner read
        // as a repeat of this advisory rather than as a different fact.
        // `noRoles` was already moved out for exactly this reason; `roleMissing`
        // was left behind. Same rule, one case short.
        if (partial) blockedReasons.push('criterion label/description');
        const blocked = blockedReasons.length > 0;
        // Mirrors the backend cross-field check (HTTP 400 from ADR D2):
        // redo_xp must be a non-negative integer and never exceed xp_value.
        const redoXpInvalid =
          !Number.isInteger(draft.redo_xp) ||
          draft.redo_xp < 0 ||
          draft.redo_xp > draft.xp_value;
        // Same shape for fail_xp — independent of redo_xp, both bounded by
        // xp_value (ADR D4: two independent checks, not a combined helper).
        const failXpInvalid =
          !Number.isInteger(draft.fail_xp) ||
          draft.fail_xp < 0 ||
          draft.fail_xp > draft.xp_value;
        // Only claim "this tenant has no roles" when the fetch SUCCEEDED and
        // came back empty. When it failed we know nothing, so we say that
        // instead (feat-251 AC21).
        const noRoles =
          existingId === null && !rolesLoadFailed && roleOptions.length === 0;
        return (
          <>
            {/* design/NOTES.md §6a — NOT `attention-*`. A failed roles fetch is a
                fault the author cannot fix from this panel, which is exactly what
                `danger-*` is reserved for (TOKENS §1o). Putting it in the family
                that promises "you authored this and you can fix it" would also
                quietly undo feat-251 AC21's distinction between "no roles exist"
                and "we don't know whether roles exist". Only the raw
                `amber-*` utilities change; the copy is untouched. */}
            {existingId === null && rolesLoadFailed && (
              <div
                role="alert"
                className="text-xs text-danger-text bg-danger-surface border border-danger-line/40 rounded p-2"
              >
                Couldn't load the roles for this tenant, so the Role list is
                empty for now. This is a load error, not a setting — reload the
                page before concluding no roles are set up.
              </div>
            )}
            {/* feat-259 / design/NOTES.md §6 + §6a — retokenised OFF the raw
                utilities `tokens.md` forbids, and split by who can fix the
                problem. `attention-*` = "you authored this and you can fix it
                HERE" (the XP bounds, the fill-in advisory). `danger-*` = a fault
                the author cannot fix from this panel (the two role conditions
                below). `warning-*` cannot carry text at all — `warning-on` on
                `warning-surface` is 1.999:1, feat-248. */}
            {/* Also NOT `attention-*` (§6a): a tenant with no active prompt
                template version is a Prompt Management setup task, not something
                this panel can offer a fix for. Copy untouched; only the raw
                `red-*` utilities change. */}
            {noRoles && (
              <div className="text-xs text-danger-text bg-danger-surface border border-danger-line/40 rounded p-2">
                This tenant has no active prompt template versions. Set up an
                active version (in Prompt Management) for at least one role
                before creating rehearsals.
              </div>
            )}
            {/* The XP advisories stay on screen as they always did. Under
                auto-save they are no longer the thing that STOPS the write —
                `validateFields` holds the offending field back and the shared
                banner explains it. These name the rule; the banner names the
                consequence. */}
            {redoXpInvalid && (
              <div
                role="alert"
                className="text-xs text-attention-on bg-attention-surface border border-attention-line/50 rounded p-2"
              >
                Redo XP must be a non-negative whole number and cannot exceed
                XP value ({draft.xp_value}).
              </div>
            )}
            {failXpInvalid && (
              <div
                role="alert"
                className="text-xs text-attention-on bg-attention-surface border border-attention-line/50 rounded p-2"
              >
                Fail XP must be a non-negative whole number and cannot exceed
                XP value ({draft.xp_value}).
              </div>
            )}
            {/* spec §Incomplete items save anyway — this notice used to gate the
                Save button. It gates NOTHING now; it describes what still needs
                doing. `noRoles` is the one exception and it is handled as a
                CREATE GATE in the save controller, not here. */}
            {blocked && !noRoles && (
              <div className="text-xs text-attention-on bg-attention-surface border border-attention-line/50 rounded p-2">
                Fill in {blockedReasons.join(', ')} to finish this rehearsal.
              </div>
            )}
            {/* feat-231: pinned action row. Unlike QuestionDetailForm — whose
                footer is a sibling of its scroll area — this editor's fields are
                flat siblings with no wrapper to scroll, so hoisting the row out
                of the flow would mean restructuring every call site. `sticky
                bottom-0` gets the same user-visible result (the row never leaves
                the pane's visible box) and degrades to plain static flow where
                nothing scrolls.

                Call sites — verified 2026-08-11, `<RehearsalEditor` in src/:
                  • CurriculumEditor's right pane — the ONLY live one, and the
                    only one that scrolls.
                  • RehearsalItemRow and NewRehearsalForm, both reached via
                    LessonBody ← LessonBlock ← UnitBlock, and UnitBlock is
                    imported by three jest files and NO route. Test-only.
                RehearsalTestHarnessPage does NOT embed this component.

                `bg-purple-50` is the container's own colour, so the row is
                opaque without introducing one.

                feat-259 / design/NOTES.md §4 — this mechanism STAYS, and the
                ruling is explicit that it stays: the constraint the comment
                above describes still holds. What converges is the row's
                STRUCTURE (justify-end + mr-auto → the same justify-between
                every other panel uses), its SCALE (text-xs px-2 py-1 → the
                shared text-sm px-3 py-2), its INK (text-red-700, a raw utility,
                → text-danger-text) and its COPY ("Remove rehearsal" → "Delete",
                since every type now ends in the same footer). The residual
                purple against the other four panels' cream is a documented
                Known inconsistency, proposed as its own re-skin feature —
                NOT to be fixed in flight.

                The error banner is inside the sticky block rather than above it
                so the two move together; a banner that scrolls away from the
                footer it belongs to is the thing error-states.html §"Growth eats
                the scroll area" rules out. */}
            <div className="sticky bottom-0 -mx-3 -mb-3 rounded-b bg-purple-50 border-t border-purple-200">
              <SaveErrorBanner
                error={save.error as never}
                blockedReason={save.blockedReason}
                // State G's second sentence, and only while the row does not
                // exist — "nothing has been saved yet" is false once it does.
                blockedDetail={existingId === null ? AUTO_SAVE_COPY.createNothingSaved : null}
                // `attention` promises a fix available here. Picking a role is;
                // a tenant with no active prompt version is not.
                blockedTone={roleOptions.length === 0 ? 'danger' : 'attention'}
                pendingNavigation={pendingNavigation}
                typeSingular={QUIZ_TERMS.rehearsal.singular}
                onRetry={pendingNavigation ? handleNavRetry : save.retry}
                onLeaveAnyway={onNavigationProceed}
              />
              <ItemDetailFooter
                onDelete={existingId !== null && onDelete ? handleDelete : undefined}
                status={save.status}
                savedAt={save.savedAt}
                containerClassName="px-3 py-2 flex items-center justify-between gap-3"
              />
            </div>
          </>
        );
      })()}
    </div>
  );
};

// ---------- Collapsed rehearsal row (expand to edit) ----------

export const RehearsalItemRow: React.FC<{
  item: RehearsalLessonItem;
  tenantId: string;
  availableRoles: AvailableRoles;
  onChanged: () => void;
}> = ({ item, tenantId, availableRoles, onChanged }) => {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <RehearsalEditor
        tenantId={tenantId}
        lessonId={item.drill_id ?? ''}
        existingId={item.id}
        pinnedVersionNumber={item.prompt_template_version_number}
        availableRoles={availableRoles}
        initial={{
          name: item.name,
          role: item.role,
          scenario_text: item.scenario_text,
          scenario_image_s3_key: item.scenario_image_s3_key,
          // Fall back to '' for payloads from a pre-feat-033 backend so the
          // editor opens cleanly during/just after rollout.
          location_label: item.location_label ?? '',
          correct_example_response: item.correct_example_response ?? '',
          xp_value: item.xp_value,
          // Fall back to 0 for payloads from a pre-redo_xp / pre-fail_xp
          // backend so the editor opens cleanly during/just after rollout.
          redo_xp: item.redo_xp ?? 0,
          fail_xp: item.fail_xp ?? 0,
          max_recording_seconds: item.max_recording_seconds,
          sort_order: item.sort_order,
          criteria: item.criteria.map((c) => ({ ...c })),
        }}
        onCreated={() => {
          setEditing(false);
          onChanged();
        }}
        onDelete={async () => {
          if (!window.confirm(`Remove rehearsal "${item.name}"?`)) return;
          await deleteRehearsal(item.id);
          setEditing(false);
          onChanged();
        }}
      />
    );
  }

  return (
    <div
      className="border-l-4 border-purple-400 bg-purple-50 rounded p-3 cursor-pointer hover:bg-purple-100"
      onClick={() => setEditing(true)}
    >
      <div className="flex items-center gap-2">
        <span className="text-xs font-semibold text-purple-700 uppercase tracking-wide">
          🎙️ Rehearsal
        </span>
        <span className="text-sm font-medium text-ink truncate">
          {item.name || '(unnamed)'}
        </span>
        <span className="ml-auto text-xs text-purple-700">
          {item.xp_value} XP
          {item.redo_xp > 0 ? ` · ${item.redo_xp} redo` : ''}
          {item.fail_xp > 0 ? ` · ${item.fail_xp} fail` : ''}
          {' '}· max {item.max_recording_seconds}s · sort {item.sort_order}
        </span>
      </div>
      {item.scenario_text && (
        <div className="text-xs text-inkMuted mt-1 truncate">
          {item.scenario_text.slice(0, 140)}
          {item.scenario_text.length > 140 ? '…' : ''}
        </div>
      )}
    </div>
  );
};

// ---------- New rehearsal create form ----------

const NewRehearsalForm: React.FC<{
  tenantId: string;
  lessonId: string;
  defaultSort: number;
  availableRoles: AvailableRoles;
  onCreated: () => void;
}> = ({ tenantId, lessonId, defaultSort, availableRoles, onCreated }) => {
  const [opening, setOpening] = useState(false);
  if (!opening) {
    return (
      <button
        onClick={() => setOpening(true)}
        className="px-3 py-2 rounded border border-dashed border-purple-300 text-sm text-purple-700 hover:bg-purple-50"
      >
        + Add rehearsal
      </button>
    );
  }
  return (
    <RehearsalEditor
      tenantId={tenantId}
      lessonId={lessonId}
      existingId={null}
      pinnedVersionNumber={null}
      availableRoles={availableRoles}
      initial={emptyRehearsalDraft(defaultSort, availableRoles ?? [])}
      onCreated={() => {
        setOpening(false);
        onCreated();
      }}
    />
  );
};

const LessonBody: React.FC<{
  lesson: QuizLesson;
  tenantId: string;
  availableRoles: AvailableRoles;
  onChanged: () => void;
}> = ({ lesson, tenantId, availableRoles, onChanged }) => {
  const items = interleaveItems(lesson);
  const maxSort = Math.max(
    0,
    ...lesson.question_items.map((q) => q.sort_order),
    ...lesson.rehearsal_items.map((r) => r.sort_order),
  );
  return (
    <div className="px-3 pb-3 space-y-2">
      <div className="text-xs text-gray-600">
        {lesson.question_items.length} question
        {lesson.question_items.length === 1 ? '' : 's'}
        {lesson.rehearsal_items.length > 0 && (
          <>
            {' · '}
            {lesson.rehearsal_items.length} rehearsal
            {lesson.rehearsal_items.length === 1 ? '' : 's'}
          </>
        )}
      </div>
      {items.map((it) =>
        it.kind === 'question' ? (
          <QuestionRow key={`q-${it.q.id}`} question={it.q} onSaved={onChanged} />
        ) : it.kind === 'rehearsal' ? (
          <RehearsalItemRow
            key={`r-${it.r.id}`}
            item={it.r}
            tenantId={tenantId}
            availableRoles={availableRoles}
            onChanged={onChanged}
          />
        ) : it.kind === 'video' ? (
          // feat-165: this legacy inline LessonBody is test-only (the live editor
          // is the feat-107 3-pane CurriculumEditor, which renders video items via
          // VideoDetailForm). Nothing to render for a video item here.
          <React.Fragment key={`v-${it.v.id}`} />
        ) : (
          // feat-166: same — reading items only render in the 3-pane editor
          // (ReadingDetailForm). Nothing to render in this test-only surface.
          <React.Fragment key={`rd-${it.rd.id}`} />
        ),
      )}
      <NewQuestionForm
        lessonId={lesson.id}
        onCreated={onChanged}
        defaultSort={maxSort + 1}
      />
      <NewRehearsalForm
        tenantId={tenantId}
        lessonId={lesson.id}
        defaultSort={maxSort + 1}
        availableRoles={availableRoles}
        onCreated={onChanged}
      />
    </div>
  );
};

// ============================================================
// Unit block
// ============================================================

export const UnitBlock: React.FC<{
  unit: QuizUnit;
  tenantId: string;
  availableRoles: AvailableRoles;
  onChanged: () => void;
}> = ({ unit, tenantId, availableRoles, onChanged }) => {
  const [expanded, setExpanded] = useState(true);
  const [addingLesson, setAddingLesson] = useState(false);
  const [lessonDraft, patchLessonDraft, resetLessonDraft] = usePatchedState<{
    title: string;
    emoji: string;
    icon_key: string | null;
    xp_reward: number;
    question_count: number | null;
    sort_order: number;
  }>({
    title: '',
    emoji: '',
    icon_key: null,
    xp_reward: 25,
    question_count: null,
    sort_order: unit.drills.length + 1,
  });

  const [meta, patchMeta, resetMeta] = usePatchedState<{
    title: string;
    subtitle: string;
    emoji: string;
    mascot_key: string | null;
    sort_order: number;
    active: boolean;
  }>({
    title: unit.title,
    subtitle: unit.subtitle ?? '',
    emoji: unit.emoji,
    mascot_key: unit.mascot_key,
    sort_order: unit.sort_order,
    active: unit.active,
  });
  const [savingMeta, setSavingMeta] = useState(false);

  useEffect(() => {
    resetMeta({
      title: unit.title,
      subtitle: unit.subtitle ?? '',
      emoji: unit.emoji,
      mascot_key: unit.mascot_key,
      sort_order: unit.sort_order,
      active: unit.active,
    });
  }, [unit, resetMeta]);

  const dirty =
    meta.title !== unit.title ||
    meta.subtitle !== (unit.subtitle ?? '') ||
    meta.emoji !== unit.emoji ||
    meta.mascot_key !== unit.mascot_key ||
    meta.sort_order !== unit.sort_order ||
    meta.active !== unit.active;

  const handleSaveMeta = async () => {
    setSavingMeta(true);
    try {
      await updateUnit(unit.id, meta);
      onChanged();
    } finally {
      setSavingMeta(false);
    }
  };

  const handleDeleteUnit = async () => {
    if (!window.confirm(`Delete unit "${unit.title}" and ALL its lessons and questions?`)) return;
    await deleteUnit(unit.id);
    onChanged();
  };

  const handleCreateLesson = async () => {
    if (!lessonDraft.title.trim()) return;
    await createLesson(unit.id, lessonDraft);
    resetLessonDraft({
      title: '',
      emoji: '',
      icon_key: null,
      xp_reward: 25,
      question_count: null,
      sort_order: unit.drills.length + 2,
    });
    setAddingLesson(false);
    onChanged();
  };

  return (
    <div className="border rounded-lg shadow-sm bg-white">
      <div className="p-3 border-b bg-red-50 space-y-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-gray-600 hover:text-gray-900 text-sm w-4"
          >
            {expanded ? '▼' : '▶'}
          </button>
          <input
            className="border rounded px-2 py-1 text-sm w-14 text-center"
            placeholder="🍹"
            value={meta.emoji}
            onChange={(e) => patchMeta({ emoji: e.target.value })}
          />
          <input
            className="border rounded px-2 py-1 text-sm flex-1 font-semibold"
            placeholder="Unit title"
            value={meta.title}
            onChange={(e) => patchMeta({ title: e.target.value })}
          />
          <CatalogSelect
            label="Mascot"
            emptyLabel="None"
            catalog={UNIT_MASCOT_CATALOG}
            value={meta.mascot_key}
            onChange={(mascot_key) => patchMeta({ mascot_key })}
          />
          <label className="text-xs text-gray-600 flex items-center gap-1">
            Sort
            <input
              type="number"
              className="border rounded px-2 py-1 text-sm w-14"
              value={meta.sort_order}
              onChange={(e) => patchMeta({ sort_order: parseInt(e.target.value) || 0 })}
            />
          </label>
          <label className="text-xs text-gray-600 flex items-center gap-1">
            <input
              type="checkbox"
              checked={meta.active}
              onChange={(e) => patchMeta({ active: e.target.checked })}
            />
            Active
          </label>
          <button
            onClick={handleSaveMeta}
            disabled={!dirty || savingMeta}
            className="px-2 py-1 rounded bg-blue-600 text-white text-xs hover:bg-blue-700 disabled:opacity-40"
          >
            {savingMeta ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={handleDeleteUnit}
            className="px-2 py-1 text-xs text-red-600 hover:text-red-800"
          >
            Delete unit
          </button>
        </div>
        {/* Subtitle: optional one-liner shown under the title on the Learn
            screen. Auto-translates via the feat-018 pipeline on save. */}
        <div className="flex items-center gap-2 pl-6">
          <span className="text-xs text-gray-500 w-14 text-right shrink-0">Subtitle</span>
          <input
            className="border rounded px-2 py-1 text-sm flex-1"
            placeholder="Optional subtitle shown under the title (translates automatically)"
            value={meta.subtitle}
            onChange={(e) => patchMeta({ subtitle: e.target.value })}
          />
        </div>
      </div>

      {expanded && (
        <div className="p-3 space-y-2">
          {unit.drills.map((l) => (
            <LessonBlock
              key={l.id}
              lesson={l}
              tenantId={tenantId}
              availableRoles={availableRoles}
              onChanged={onChanged}
            />
          ))}
          {!addingLesson ? (
            <button
              onClick={() => setAddingLesson(true)}
              className="px-3 py-2 rounded border border-dashed border-gray-300 text-sm text-gray-600 hover:bg-gray-50 w-full"
            >
              + Add lesson
            </button>
          ) : (
            <div className="border-2 border-dashed border-blue-200 rounded p-3 bg-blue-50">
              <div className="text-xs font-medium text-blue-700 mb-2">New lesson</div>
              <div className="flex gap-2 mb-2">
                <input
                  className="border rounded px-2 py-1 text-sm flex-1"
                  placeholder="Lesson title"
                  value={lessonDraft.title}
                  onChange={(e) => patchLessonDraft({ title: e.target.value })}
                />
                <label className="text-xs text-gray-600 flex items-center gap-1">
                  XP
                  <input
                    type="number"
                    className="border rounded px-2 py-1 text-sm w-16"
                    value={lessonDraft.xp_reward}
                    onChange={(e) =>
                      patchLessonDraft({ xp_reward: parseInt(e.target.value) || 0 })
                    }
                  />
                </label>
                <label
                  className="text-xs text-gray-600 flex items-center gap-1"
                  title="Questions per session. Blank = show all."
                >
                  Qs/session
                  <input
                    type="number"
                    min={1}
                    max={100}
                    placeholder="All"
                    className="border rounded px-2 py-1 text-sm w-16"
                    value={lessonDraft.question_count ?? ''}
                    onChange={(e) => {
                      const v = e.target.value.trim();
                      if (v === '') {
                        patchLessonDraft({ question_count: null });
                      } else {
                        const n = parseInt(v, 10);
                        patchLessonDraft({ question_count: Number.isFinite(n) ? n : null });
                      }
                    }}
                  />
                </label>
              </div>
              <div className="mb-2">
                <IconKeyPicker
                  iconKey={lessonDraft.icon_key}
                  emoji={lessonDraft.emoji}
                  onIconKey={(icon_key) => patchLessonDraft({ icon_key })}
                  onEmoji={(emoji) => patchLessonDraft({ emoji })}
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setAddingLesson(false)}
                  className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateLesson}
                  disabled={!lessonDraft.title.trim()}
                  className="px-3 py-1 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-40"
                >
                  Create lesson
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================
// Page root
// ============================================================

// ============================================================
// feat-107 Slice A — Master–Detail Tree editor
// ============================================================

type Selection =
  | { kind: 'none' }
  | { kind: 'question'; id: string }
  | { kind: 'rehearsal'; id: string }
  // feat-165: a selected explanatory video item.
  | { kind: 'video'; id: string }
  // feat-166: a selected reading item.
  | { kind: 'reading'; id: string }
  // feat-109: a single "new" draft; the detail pane's Type control picks the
  // concrete type. itemType 'rehearsal' → rehearsal form; 'video' → video form;
  // 'reading' → reading form; otherwise the question form seeded with that type.
  | { kind: 'new'; itemType: DetailItemType };

function rehearsalToDraft(r: RehearsalLessonItem): RehearsalDraft {
  return {
    name: r.name,
    role: r.role,
    scenario_text: r.scenario_text,
    scenario_image_s3_key: r.scenario_image_s3_key,
    location_label: r.location_label ?? '',
    correct_example_response: r.correct_example_response ?? '',
    xp_value: r.xp_value,
    redo_xp: r.redo_xp ?? 0,
    fail_xp: r.fail_xp ?? 0,
    max_recording_seconds: r.max_recording_seconds,
    sort_order: r.sort_order,
    criteria: r.criteria.map((c) => ({ ...c })),
  };
}

function firstItemSelection(drill: QuizLesson): Selection {
  const its = interleaveItems(drill);
  if (its.length === 0) return { kind: 'none' };
  const f = its[0];
  if (f.kind === 'question') return { kind: 'question', id: f.q.id };
  if (f.kind === 'rehearsal') return { kind: 'rehearsal', id: f.r.id };
  if (f.kind === 'video') return { kind: 'video', id: f.v.id };
  return { kind: 'reading', id: f.rd.id };
}

/**
 * feat-108 (ADR 0023) — Lesson custom-image control. REPLACES the unit mascot
 * IconPickerPopover in the Lesson meta panel. Lets an admin upload an arbitrary
 * image (png/jpeg/webp ≤ 2 MB) that the Flutter unit card and this preview render
 * IN PLACE OF the mascot art. Precedence in the preview swatch: `image_s3_key`
 * (uploaded image) → `mascot_key` (catalog glyph) → `emoji` → 🍔 fallback —
 * matching the cross-app contract. "Remove" nulls `image_s3_key`, so the unit
 * falls back to its existing mascot/emoji (no data loss; the column is additive).
 *
 * S3 hard-enforces the type allowlist + size; the client pre-checks only for a
 * friendly message before the presign round-trip. In `fake` mode there is no
 * backend, so the uploader is inert (the build/preview still work).
 */
export const LessonImageControl: React.FC<{
  unit: QuizUnit;
  tenantId: string;
  onSave: (patch: Partial<{ image_s3_key: string | null }>) => Promise<void>;
}> = ({ unit, tenantId, onSave }) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fakeMode = config.environment === 'fake';

  const imageUrl = lessonImagePublicUrl(unit.image_s3_key);
  const mascotGlyph = unit.mascot_key ? glyphForKey(unit.mascot_key) : null;
  const fallbackGlyph = mascotGlyph ?? (unit.emoji || '🍔');

  const handleSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Clear the input so re-picking the SAME file fires onChange again.
    e.target.value = '';
    if (!file) return;
    setError(null);

    if (!LESSON_IMAGE_ALLOWED_TYPES.includes(file.type)) {
      setError('Unsupported file type. Use a PNG, JPEG, or WebP image.');
      return;
    }
    if (file.size > LESSON_IMAGE_MAX_BYTES) {
      setError('Image is too large. Max size is 2 MB.');
      return;
    }

    setUploading(true);
    try {
      const { s3_key } = await uploadLessonImage(file, tenantId, unit.id);
      await onSave({ image_s3_key: s3_key });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    setError(null);
    setUploading(true);
    try {
      await onSave({ image_s3_key: null });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      {/* Preview swatch — image → mascot glyph → emoji → 🍔 */}
      <div className="w-12 h-12 rounded-xl bg-gray-100 grid place-items-center overflow-hidden shrink-0 border border-softBorder">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={unit.title || QUIZ_TERMS.lesson.singular}
            className="w-full h-full object-cover"
          />
        ) : (
          <span className="text-xl" aria-hidden>{fallbackGlyph}</span>
        )}
      </div>

      <div className="flex flex-col gap-1">
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="hidden"
          onChange={handleSelect}
          disabled={uploading || fakeMode}
        />
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading || fakeMode}
            className="text-xs font-semibold rounded-full px-3 py-1.5 bg-coral text-white hover:brightness-95 disabled:opacity-50"
          >
            {uploading ? 'Uploading…' : imageUrl ? 'Replace image' : 'Upload image'}
          </button>
          {imageUrl && (
            <button
              type="button"
              onClick={handleRemove}
              disabled={uploading || fakeMode}
              className="text-xs font-semibold text-coral hover:underline disabled:opacity-50"
            >
              Remove
            </button>
          )}
        </div>
        {error ? (
          <span className="text-xs text-danger-text">{error}</span>
        ) : fakeMode ? (
          <span className="text-xs text-inkMuted">Upload unavailable in demo mode</span>
        ) : (
          <span className="text-xs text-inkMuted">PNG, JPEG, or WebP · max 2 MB</span>
        )}
      </div>
    </div>
  );
};

/**
 * feat-187 (R1, D1–D3) — the Lesson's glyph control, restored next to the image
 * uploader. feat-108 swapped the unit `IconPickerPopover` out for
 * `LessonImageControl`, which left `emoji`/`mascot_key` unreachable from the UI
 * even though both round-trip through `PUT /learn-lessons/{id}` — and the glyph is
 * what the left navigator renders, so it is the thing you scan.
 *
 * D2 — ONE glyph source at a time: a catalog pick writes `mascot_key` and NULLs
 * `emoji`; a custom emoji writes `emoji` and NULLs `mascot_key`. Without that,
 * the navigator's `mascot_key → emoji` precedence keeps showing the old mascot
 * and the save reads as a no-op. `image_s3_key` is NEVER touched here (D3): the
 * image and the glyph are independent, and removing the image reveals the glyph.
 *
 * Note this control is live in `fake` mode — unlike the image uploader (which is
 * inert there because it needs a presign round-trip), the emoji/mascot save goes
 * through the ordinary patch path.
 */
export const LessonGlyphControl: React.FC<{
  unit: QuizUnit;
  onSave: (patch: Partial<{ mascot_key: string | null; emoji: string | null }>) => Promise<void>;
}> = ({ unit, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  // Mirrors the navigator/panel precedence: mascot wins, then a free-form emoji,
  // else nothing picked yet (the picker shows its ＋ placeholder).
  const selection: IconSelection | null = unit.mascot_key
    ? { kind: 'catalog', key: unit.mascot_key }
    : unit.emoji
    ? { kind: 'custom', emoji: unit.emoji }
    : null;

  const handleSelect = async (sel: IconSelection) => {
    setError(null);
    setNote(null);

    let patch: { mascot_key: string | null; emoji: string | null };
    let trimmedTo: string | null = null;

    if (sel.kind === 'catalog') {
      patch = { mascot_key: sel.key, emoji: null };
    } else {
      // VARCHAR(10): keep one grapheme cluster, refuse what can't fit (R1).
      const normalized = normalizeUnitEmoji(sel.emoji);
      if (!normalized.ok) {
        setError(normalized.message);
        return;
      }
      patch = { mascot_key: null, emoji: normalized.emoji };
      if (normalized.trimmed) trimmedTo = normalized.emoji;
    }

    setSaving(true);
    try {
      await onSave(patch);
      if (trimmedTo) {
        setNote(`Saved ${trimmedTo} — one emoji per ${QUIZ_TERMS.lesson.singular}.`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <IconPickerPopover
        catalog={UNIT_MASCOT_CATALOG}
        selection={selection}
        glyphForKey={glyphForKey}
        disabled={saving}
        ariaLabel={`Change ${QUIZ_TERMS.lesson.singular} emoji`}
        onSelect={handleSelect}
      />
      <div className="flex flex-col">
        <span className="text-xs font-bold text-inkMuted">Emoji</span>
        {error ? (
          <span className="text-xs text-danger-text max-w-[220px]">{error}</span>
        ) : note ? (
          <span className="text-xs text-inkMuted max-w-[220px]">{note}</span>
        ) : (
          <span className="text-xs text-inkMuted">Shown in the tree</span>
        )}
      </div>
    </div>
  );
};

/** Center panel shown when a Lesson (unit) is selected but no Drill — edit the
 *  Lesson's image/glyph/title/subtitle/active, and reach its Drills.
 *  Exported for tests (same pattern as `LessonImageControl`): the glyph picker
 *  being wired into THIS panel, next to the uploader, is feat-187 R1's actual
 *  requirement — a control that works in isolation but isn't mounted here is
 *  exactly the gap feat-108 left behind. */
/**
 * feat-354-auto-copy-lessons — "Copy of <original>" under the Lesson identity row
 * (design NOTES §2b/§2c). Renders NOTHING for an original (no reserved space).
 * The link is ink, not coral (coral fails AA at 12px — NOTES §1).
 */
export const LessonCopyOfLine: React.FC<{
  unit: QuizUnit;
  onOpenOriginal?: (lessonId: string) => void;
}> = ({ unit, onOpenOriginal }) => {
  const info = copyOfInfo(unit);
  if (info.kind === 'original') return null;
  return (
    <p className="flex items-center gap-1 text-xs text-inkMuted min-w-0 mt-1" data-testid="lesson-copy-of">
      <CopyIcon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
      {info.kind === 'deleted' ? (
        <span className="truncate min-w-0">{COPY_OF_DELETED}</span>
      ) : (
        <>
          <span className="shrink-0">{COPY_OF_LEAD}</span>
          {/* A real space for text/AT ("Copy of Soup Basics"); a whitespace-only
              flex child is not rendered, so the visual gap stays `gap-1`. */}
          {' '}
          {onOpenOriginal ? (
            <button
              type="button"
              onClick={() => onOpenOriginal(info.originalId)}
              title={openOriginalTitle(info.originalTitle)}
              data-testid="lesson-copy-of-link"
              className="font-semibold text-ink underline decoration-ink/30 underline-offset-2 hover:decoration-ink truncate min-w-0 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-coral/40"
            >
              {info.originalTitle}
            </button>
          ) : (
            <span className="font-semibold text-ink truncate min-w-0">{info.originalTitle}</span>
          )}
        </>
      )}
    </p>
  );
};

export const LessonMetaPanel: React.FC<{
  /** feat-270 panel 6 — null when the approval surface is absent (AT-29). */
  approvalRow?: LessonApprovalRow | null;
  unit: QuizUnit;
  tenantId: string;
  onSave: (patch: Partial<{ title: string; subtitle: string; mascot_key: string | null; image_s3_key: string | null; emoji: string | null; active: boolean; available_at: string | null; drip_bypass: boolean }>) => Promise<void>;
  onDelete: () => void;
  /** feat-235 — every Track this Lesson is in, for the read-only membership
   *  strip (mockup §8). OPTIONAL: when the tracks payload failed to load, or in
   *  a test that does not care, the panel renders exactly as it did before. */
  tracks?: LearnTrack[];
  /** Jump to the Tracks tab with that track selected. */
  onOpenTrack?: (trackId: string) => void;
  /** feat-354 — select the original of an auto-copied Lesson (same Lessons tab).
   *  Absent ⇒ the "Copy of" line renders the title as plain text, no link. */
  onOpenOriginal?: (lessonId: string) => void;
}> = ({ unit, tenantId, onSave, onDelete, tracks, onOpenTrack, onOpenOriginal, approvalRow = null }) => {
  const [title, setTitle] = useState(unit.title);
  const [subtitle, setSubtitle] = useState(unit.subtitle ?? '');
  /** feat-256 followup — the clip box for the Release date popover (see below). */
  const paneRef = useRef<HTMLElement>(null);
  useEffect(() => {
    setTitle(unit.title);
    setSubtitle(unit.subtitle ?? '');
  }, [unit.id, unit.title, unit.subtitle]);

  return (
    // feat-231: same treatment as DrillChildList, which occupies this same
    // middle grid slot. The panel's controls — Active and "Delete Lesson" —
    // live in the meta strip at the TOP rather than in a footer row, so pinning
    // the header (not a footer) is what keeps them permanently reachable.
    // feat-256 followup: `paneRef` is the CLIP BOX the Release date popover
    // clamps itself inside. The `overflow-hidden` on this line is what was
    // cutting the popover off — and it must STAY, so the fix is to keep the
    // popover inside it by construction rather than to remove it. Per the
    // comment at the card (`minHeight: 480` grid below), dropping it makes the
    // card's min-content height the panes' full content height and the whole
    // page scrolls again: that is the feat-231 bug, restored, to solve a
    // positioning problem.
    <section ref={paneRef} className="flex flex-col min-h-0 overflow-hidden">
      <div className="px-6 pt-6 pb-4 shrink-0 space-y-4">
        {/* feat-354 (design NOTES §2b): the identity row and the "Copy of" line
            share ONE child of `space-y-4`, so copies pay one 16px line (mt-1)
            and originals pay nothing. NOT in the cream meta strip below. */}
        <div>
        <div className="flex items-center gap-2 text-sm">
          {/* feat-252: `truncate min-w-0` added because the timestamp below is an
              `ml-auto` sibling — without it a long title squeezes the timestamp
              instead of ellipsing itself (spec §User-visible behavior, NOTES §5).
              This is the only pre-existing markup feat-252 changes. */}
          <span className="text-ink font-bold truncate min-w-0">
            <span className="text-inkMuted/60 text-xs mr-1 font-normal">{QUIZ_TERMS.lesson.singular}</span>
            {unit.title || '(untitled)'}
          </span>
          {/* feat-252: rides the identity row's free trailing edge — zero vertical
              cost. NOT in the cream meta strip below: that strip is `flex-wrap`
              and pinned, so one more child wraps it and costs list rows. */}
          <ContentTimestamps variant="inline" created={unit.created} />
          {/* feat-270 panel 6 — rides this row's free trailing edge, at ZERO
              vertical cost, for the same reason `ContentTimestamps` does. NOT in
              the cream meta strip below: that strip is `flex-wrap` and pinned, so
              one more child wraps it and costs list rows. Look one row up. */}
          <LessonApprovalStamp row={approvalRow} />
        </div>
        <LessonCopyOfLine unit={unit} onOpenOriginal={onOpenOriginal} />
        </div>

        <div className="bg-cream/70 border border-softBorder rounded-2xl p-3 flex items-center gap-3 flex-wrap">
          <LessonImageControl unit={unit} tenantId={tenantId} onSave={onSave} />
          <LessonGlyphControl unit={unit} onSave={onSave} />
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={() => {
              const next = title.trim();
              if (next && next !== unit.title) onSave({ title: next });
              else setTitle(unit.title);
            }}
            className="flex-1 min-w-[180px] bg-transparent font-display text-lg font-semibold px-1 py-1 rounded-lg hover:bg-white focus:bg-white focus:outline-none focus:ring-2 focus:ring-coral/40"
          />
          <button
            type="button"
            onClick={() => onSave({ active: !unit.active })}
            className={`text-xs font-bold rounded-full px-2.5 py-1 flex items-center gap-1.5 ${
              unit.active ? 'bg-success-light text-success-on' : 'bg-gray-100 text-inkMuted'
            }`}
          >
            <span className={`w-2 h-2 rounded-full ${unit.active ? 'bg-success' : 'bg-inkMuted'}`} />
            {unit.active ? 'Active' : 'Hidden'}
          </button>
          {/* feat-256 — the ONE new child of this strip (design notes §3.1), and it
              belongs here specifically: it modifies the status the pill above sets.
              After Delete would separate two status controls with a destructive
              action between them; before Active would split Active from its own
              dot-and-label pattern.

              feat-252's comment above correctly warns that one more child in this
              flex-wrap strip costs list rows. That cost was MEASURED (notes §6) and
              is 0px down to a 1280px window for the empty state — which is the
              state all 41 existing Lessons are in and the state AC22 tests. A
              scheduled Lesson costs one flex line below 1440px, paid only by
              Lessons somebody deliberately scheduled. feat-231's pinned header
              still holds: worst measured strip is 166px inside a >=480px pane. */}
          <LessonScheduleControl unit={unit} onSave={onSave} clipRef={paneRef} />
          {/* feat-303 — Skip drip (design NOTES §4): right after Release date, before
              Delete. MEASURED (feat-256 §6 method: real rendered strip + compiled
              Tailwind + Nunito, headless Chromium): at a 1440 window (668px strip)
              it adds 0px in every state, so the pill ships, not the F-ALT row. At
              1280 (508px) an UNscheduled Lesson's strip gains one flex line
              (122→154px); a scheduled one already wraps and gains 1px. */}
          <LessonSkipDripToggle unit={unit} onSave={onSave} />
          <button type="button" onClick={onDelete} className="text-coral text-sm font-semibold hover:underline">
            Delete {QUIZ_TERMS.lesson.singular}
          </button>
        </div>
      </div>

      {/* the ONLY scrolling region of this pane */}
      <div className="px-6 pb-6 flex-1 min-h-0 overflow-y-auto space-y-4">
        {/* feat-235 — read-only; see LessonTracksStrip for why it is not an editor. */}
        {tracks && (
          <LessonTracksStrip
            lessonId={unit.id}
            tracks={tracks}
            onOpenTrack={onOpenTrack}
            availableAt={unit.available_at ?? null}
          />
        )}

        <div>
          <label className="block text-xs font-bold text-inkMuted mb-1.5">Subtitle</label>
          <input
            value={subtitle}
            onChange={(e) => setSubtitle(e.target.value)}
            onBlur={() => {
              if ((subtitle ?? '') !== (unit.subtitle ?? '')) onSave({ subtitle });
            }}
            placeholder="Optional line under the title"
            className="w-full text-sm bg-white border border-softBorder rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-coral/40"
          />
        </div>

        {unit.drills.length === 0 ? (
          <div className="bg-white border border-softBorder rounded-3xl p-8 text-center">
            <div className="text-5xl mb-3">🍢</div>
            <h4 className="font-display text-lg font-semibold">
              “{unit.title || `This ${QUIZ_TERMS.lesson.singular}`}” has no {QUIZ_TERMS.drill.plural}
            </h4>
            <p className="text-sm text-inkMuted mt-1">
              A {QUIZ_TERMS.drill.singular} groups {QUIZ_TERMS.question.plural} &amp; {QUIZ_TERMS.rehearsal.plural}. Add one from the tree to start authoring.
            </p>
          </div>
        ) : (
          <p className="text-sm text-inkMuted">Select a {QUIZ_TERMS.drill.singular} from the tree to edit its {QUIZ_TERMS.question.plural} &amp; {QUIZ_TERMS.rehearsal.plural}.</p>
        )}
      </div>
    </section>
  );
};

// feat-231: carries the SAME bounded shape as the real grid so the card does not
// resize (and the page does not jump) when content replaces the skeleton.
const ThreePaneSkeleton: React.FC = () => (
  <div
    className="grid flex-1"
    style={{
      gridTemplateColumns: '280px minmax(0,1fr) 380px',
      gridTemplateRows: 'minmax(0, 1fr)',
      minHeight: 480,
      // Same reasoning as the live grid — see the long note there.
      contain: 'size',
    }}
  >
    <div className="border-r border-softBorder p-3 space-y-2.5">
      <div className="h-9 bg-gray-100 rounded-full animate-pulse" />
      <div className="h-7 bg-gray-100 rounded-lg animate-pulse" />
      <div className="h-7 bg-gray-100 rounded-lg animate-pulse ml-4 w-5/6" />
      <div className="h-7 bg-gray-100 rounded-lg animate-pulse ml-4 w-4/6" />
      <div className="h-7 bg-gray-100 rounded-lg animate-pulse" />
    </div>
    <div className="p-5 space-y-3">
      <div className="h-5 bg-gray-100 rounded animate-pulse w-1/3" />
      <div className="h-12 bg-gray-100 rounded-2xl animate-pulse" />
      <div className="h-12 bg-gray-100 rounded-2xl animate-pulse" />
      <div className="h-12 bg-gray-100 rounded-2xl animate-pulse w-11/12" />
    </div>
    <div className="border-l border-softBorder p-5 space-y-3">
      <div className="h-6 bg-gray-100 rounded animate-pulse w-1/2" />
      <div className="h-16 bg-gray-100 rounded-xl animate-pulse" />
      <div className="h-10 bg-gray-100 rounded-xl animate-pulse" />
    </div>
  </div>
);

// ============================================================
// feat-164 — Curriculum change history + per-item undo
// ============================================================
//
// A single slide-over panel serves BOTH the per-item history affordance and the
// tenant-wide recent-change feed (the primary way to find/restore a DELETED item,
// which no longer has a tree row to open). Scope chips at the top let the author
// pick any level of the current selection chain — Lesson ▸ Drill ▸ Question /
// Rehearsal — plus "All recent". Each revertable entry shows a Revert control.
// Keep it minimal (list + revert, no diff view) per ADR 0001 frontend slice 2.

/** Map a DB `entity_type` to its UI display label. Unknown strings render
 *  verbatim.
 *
 *  feat-198: no pre-rename cases are kept. Migration 110 UPDATEs every stored
 *  `curriculum_change_history.entity_type` row (ADR 0001 §D-I), so old values do
 *  not survive the migration and a case for them would be dead code that AC7's
 *  zero-result census then has to allowlist. Across the deploy window a
 *  mismatched value falls through to `default` and renders the raw DB name —
 *  visibly odd, never silently wrong, which is the trade this file already made. */
export function entityTypeLabel(entityType: string): string {
  switch (entityType) {
    // feat-235. The two child types are named for what the author changed — a
    // membership/prereq row has no identity of its own in the editor, and the
    // history row's entity_id is the TRACK's id in both cases.
    case 'learn_track':
      return QUIZ_TERMS.track.singular;
    case 'learn_track_lesson':
      return `${QUIZ_TERMS.track.singular} ${QUIZ_TERMS.lesson.plural.toLowerCase()}`;
    case 'learn_track_prereq':
      return `${QUIZ_TERMS.track.singular} prerequisites`;
    case 'learn_lesson':
      return QUIZ_TERMS.lesson.singular;
    case 'learn_drill':
      return QUIZ_TERMS.drill.singular;
    case 'question_item':
      return QUIZ_TERMS.question.singular;
    case 'rehearsal_template':
      return QUIZ_TERMS.rehearsal.singular;
    // feat-167 (ADR D9): these landed with feat-165/166 but never got a case, so
    // history rows rendered the literal DB name "video_item"/"reading_item".
    case 'video_item':
      return 'Video';
    case 'reading_item':
      return 'Reading';
    default:
      return entityType;
  }
}

/**
 * Compact relative time. MOVED to `./curriculum/relativeTime` by feat-270 and
 * re-exported here so every existing importer and test keeps working against one
 * definition. It moved because the approval queue needs it too, and the editor
 * will import the approval components back for AT-25's header badge — which
 * would have made a direct import a cycle.
 */
export { timeAgo };

const ACTION_BADGE: Record<string, string> = {
  create: 'bg-success-light text-success-on',
  update: 'bg-blue-100 text-blue-700',
  delete: 'bg-danger-surface text-danger-text',
  revert: 'bg-warning-surface text-ink',
};

/** An item whose history can be viewed. `label` is UI copy (already localized to
 *  the feat-107 terms); `entityType` is the DB name the API keys off. */
export interface HistoryItemTarget {
  entityType: CurriculumEntityType;
  entityId: string;
  label: string;
}

type HistoryScope =
  | { kind: 'item'; target: HistoryItemTarget }
  | { kind: 'feed' };

function scopeKey(scope: HistoryScope): string {
  return scope.kind === 'feed' ? 'feed' : `${scope.target.entityType}:${scope.target.entityId}`;
}

// ============================================================
// feat-167: Before | After snapshot detail
// ============================================================
//
// THE DESIGN RULE HERE: the renderer never decides WHICH fields exist — it
// renders every column present in the stored row. Type-awareness is confined to
// ORDER and LABELS. That inversion is what makes feat-165 (video), feat-166
// (reading), and every future item type render correctly with zero changes to
// this file: a new column shows up with a humanized label at the tail, and a
// whole new `entity_type` falls back to an alphabetical key/value dump. A
// whitelist of known fields would silently hide new data instead — the one
// failure mode the spec explicitly forbids.

/** Human labels for known DB columns, seeded from the EDITOR's own wording so a
 *  manager reads the same field name in the history panel that they typed into
 *  the form (ADR D9 — where they differ, the editor wins).
 *
 *  A LOOKUP, not a whitelist: a column missing from this map still renders,
 *  humanized from its own name. Gaps are harmless by design. */
const SNAPSHOT_FIELD_LABEL: Record<string, string> = {
  title: 'Title',
  subtitle: 'Subtitle',
  emoji: 'Emoji',
  icon_key: 'Lesson icon',
  mascot_key: 'Mascot',
  image_s3_key: 'Image',
  image_s3_keys: 'Images',
  sort_order: 'Sort order',
  active: 'Active',
  archived: 'Archived',
  xp_reward: 'XP reward',
  xp_value: 'XP value',
  redo_xp: 'Redo XP',
  fail_xp: 'Fail XP',
  question_count: 'Questions per session',
  question_type: 'Question type',
  question_text: 'Question',
  option_a: 'Option A',
  option_b: 'Option B',
  option_c: 'Option C',
  option_d: 'Option D',
  correct_option: 'Correct answer',
  explanation: 'Wrong-answer explanation',
  correct_explanation: 'Right-answer praise',
  blank_accepted_variants: 'Accepted variants',
  name: 'Name',
  role: 'Role',
  scenario_text: 'Scenario text',
  scenario_image_s3_key: 'Scenario image',
  location_label: 'Location label',
  correct_example_response: 'Correct example response',
  max_recording_seconds: 'Max recording seconds',
  body: 'Body',
  published: 'Published',
  captions_status: 'Captions',
  duration_seconds: 'Duration (s)',
  video_s3_key: 'Video',
  source_s3_key: 'Source video',
  caption_en_s3_key: 'Captions (EN)',
  caption_es_s3_key: 'Captions (ES)',
  label: 'Label',
  description: 'Description',
  example_phrases: 'Example phrases',
  kind: 'Kind',
  position: 'Position',
};

/** Columns MySQL hands back as 0/1 that read as a state, not a number. Purely a
 *  presentation nicety — a column missing from this set still renders its raw
 *  value, so nothing breaks when a new flag column appears. */
const SNAPSHOT_BOOLEAN_FIELDS = new Set(['active', 'archived', 'published']);

/** feat-346: question options that may be blank on a 2–3 option question. */
const SNAPSHOT_OPTION_FIELDS = ['option_a', 'option_b', 'option_c', 'option_d'];

/** Preferred field ORDER per known type — a prefix, not a filter. Columns not
 *  listed (including every column of an unknown type) render after these,
 *  alphabetically. */
const SNAPSHOT_FIELD_ORDER: Record<string, string[]> = {
  learn_lesson: ['title', 'subtitle', 'emoji', 'mascot_key', 'icon_key', 'image_s3_key', 'sort_order', 'active'],
  learn_drill: ['title', 'emoji', 'icon_key', 'xp_reward', 'question_count', 'sort_order', 'active'],
  question_item: [
    'question_type', 'question_text',
    'option_a', 'option_b', 'option_c', 'option_d',
    'correct_option', 'blank_accepted_variants',
    'explanation', 'correct_explanation', 'xp_value', 'sort_order',
  ],
  rehearsal_template: [
    'name', 'role', 'scenario_text', 'location_label', 'correct_example_response',
    'scenario_image_s3_key', 'xp_value', 'redo_xp', 'fail_xp', 'max_recording_seconds', 'sort_order',
  ],
  video_item: ['title', 'published', 'captions_status', 'duration_seconds', 'video_s3_key', 'caption_en_s3_key', 'caption_es_s3_key', 'sort_order'],
  reading_item: ['title', 'body', 'image_s3_keys', 'sort_order'],
};

/** Identifiers and audit stamps: DEMOTED to the end in a muted "System fields"
 *  group, never DROPPED (ADR D4 — degradation is a labeling concern, never a
 *  filtering one). No manager edited these, so they shouldn't bury the fields
 *  that did change — but they stay visible and auditable. Type-agnostic. */
// feat-198: this set is ADDITIVE across the rename, and deliberately so. Unlike
// `entity_type`, the snapshot BLOBS are never rewritten — ADR 0001 §D-J refused
// to rewrite nested before/after JSON and made pre-rename snapshots
// unrevertable-but-VIEWABLE instead. So a stored blob carries whichever epoch's
// column names it was captured under, and BOTH must stay listed: drop
// `lesson_id`/`unit_id` and every pre-migration snapshot stops demoting its
// identifier columns and starts rendering them as ordinary changed fields.
const SNAPSHOT_SYSTEM_FIELDS = new Set([
  'id', 'tenant_id', 'rehearsal_template_id',
  // post-feat-198 column names
  'drill_id', 'parent_lesson_id', 'question_item_id',
  // pre-feat-198 column names — still present in older snapshots
  'lesson_id', 'unit_id', 'question_id',
  // These tables stamp `created`/`updated` (not `created_at`); accept both.
  'created', 'updated', 'created_at', 'updated_at', 'created_by',
  'prompt_template_version_id', 'prompt_template_version_number',
]);

export function snapshotFieldLabel(column: string): string {
  if (SNAPSHOT_FIELD_LABEL[column]) return SNAPSHOT_FIELD_LABEL[column];
  const spaced = column.replace(/_/g, ' ');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** The one shape the renderer sees, whatever the backend stored. */
export interface NormalizedSnapshot {
  /** The primary entity's own columns (criteria lifted out, wrapper unwrapped). */
  row: Record<string, unknown>;
  /** Rehearsal ordered criteria, when the row carried them. */
  criteria: Array<Record<string, unknown>> | null;
  /** Nested children on a DELETE subtree, summarized: [{key:'questions',count:3}]. */
  childCounts: Array<{ key: string; count: number }>;
}

/**
 * Collapse the three stored snapshot shapes into {@link NormalizedSnapshot}:
 *  - flat row dict                          → row
 *  - flat row + `criteria: [...]`           → row + criteria (rehearsals)
 *  - wrapped DELETE subtree, e.g.
 *    {learn_drill:{...}, questions:[...]}   → row = inner, childCounts = the rest
 *
 * The unwrap keys off `entityType` itself, so an unknown/future type's wrapped
 * delete normalizes correctly without being named here. Returns null for a
 * missing side (CREATE has no before; DELETE has no after).
 */
export function normalizeSnapshot(
  side: CurriculumSnapshotSide,
  entityType: string,
): NormalizedSnapshot | null {
  if (!side || typeof side !== 'object' || Array.isArray(side)) return null;

  let row: Record<string, unknown> = side;
  const childCounts: Array<{ key: string; count: number }> = [];

  // Wrapped DELETE subtree: the entity's own row lives under its type name, and
  // any sibling arrays are its children.
  const wrapped = (side as Record<string, unknown>)[entityType];
  if (wrapped && typeof wrapped === 'object' && !Array.isArray(wrapped)) {
    row = wrapped as Record<string, unknown>;
    for (const [k, v] of Object.entries(side)) {
      if (k !== entityType && Array.isArray(v)) childCounts.push({ key: k, count: v.length });
    }
  }

  // Rehearsal criteria travel inside the row; lift them so they render ordered
  // rather than as a JSON blob.
  let criteria: Array<Record<string, unknown>> | null = null;
  if (Array.isArray(row.criteria)) {
    criteria = (row.criteria as unknown[]).filter(
      (c): c is Record<string, unknown> => !!c && typeof c === 'object' && !Array.isArray(c),
    );
    const { criteria: _lifted, ...rest } = row;
    row = rest;
  }

  return { row, criteria, childCounts };
}

/**
 * Fields to render, split into the authored fields and the demoted system ones.
 *
 * Both lists come from the UNION of the two sides' columns, so a column added or
 * dropped by the change still shows (as "—" on the side that lacks it). The
 * per-type config supplies ORDER only — it never filters — so an unknown column
 * lands in the alphabetical tail and an unknown `entity_type` renders its whole
 * row alphabetically. Nothing is ever silently dropped — except (feat-346) a
 * question option that is blank on every side, which carries no information.
 */
export function orderedSnapshotFields(
  entityType: string,
  ...rows: Array<Record<string, unknown> | null | undefined>
): { fields: string[]; systemFields: string[] } {
  const present = new Set<string>();
  const system = new Set<string>();
  for (const row of rows) {
    if (!row) continue;
    for (const k of Object.keys(row)) {
      if (SNAPSHOT_SYSTEM_FIELDS.has(k)) system.add(k);
      else present.add(k);
    }
  }
  // feat-346: a 2–3 option question leaves C/D blank. An option that is blank
  // on EVERY side carries no information (it would read "—" | "—"), so it is
  // not listed. An option that changed to or from blank still shows: that is
  // a real change.
  if (entityType === 'question_item') {
    for (const k of SNAPSHOT_OPTION_FIELDS) {
      const blankEverywhere = rows.every((row) => {
        const v = row?.[k];
        return v === null || v === undefined || (typeof v === 'string' && v.trim() === '');
      });
      if (blankEverywhere) present.delete(k);
    }
  }
  const preferred = SNAPSHOT_FIELD_ORDER[entityType] ?? [];
  const head = preferred.filter((k) => present.has(k));
  const tail = Array.from(present).filter((k) => !preferred.includes(k)).sort();
  return { fields: [...head, ...tail], systemFields: Array.from(system).sort() };
}

/**
 * Render a stored value as text. Values arrive AS STORED — DECIMAL columns are
 * strings, MySQL flags are 0/1, and a MySQL `JSON` column (reading_item's
 * `image_s3_keys`) may arrive EITHER as a decoded array or as raw JSON text
 * depending on the connector. Nothing here coerces or does math; the only
 * transformations are display ones.
 *
 * `column` is optional context for the flag-column nicety — omit it and values
 * still render, just as their raw selves.
 *
 * ⚠️ DO NOT MAKE THIS TRUNCATE. `changedSnapshotFields` decides which fields to
 * mark by comparing this function's OUTPUT, and that is only sound because the
 * output is lossless: if two values format identically they are identical to the
 * manager too, so suppressing the mark hides nothing. Add truncation (an ellipsis
 * on long bodies, say) and two genuinely different long values can collide on the
 * same prefix — the mark would be suppressed for a real difference the manager
 * could still scroll to and see. Long values are handled by CSS wrapping in the
 * renderer, never by shortening the string here. (QA note, feat-167 fast-follow.)
 */
export function formatSnapshotValue(value: unknown, column?: string): string {
  if (value === null || value === undefined) return '—';

  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  // MySQL flags come back as 0/1 — "Active: Yes" beats "Active: 1".
  if (
    column &&
    SNAPSHOT_BOOLEAN_FIELDS.has(column) &&
    (value === 0 || value === 1 || value === '0' || value === '1')
  ) {
    return Number(value) === 1 ? 'Yes' : 'No';
  }

  if (typeof value === 'number') return String(value);

  if (typeof value === 'string') {
    if (value.trim() === '') return '—';
    // A JSON column may hand back text instead of a decoded value. Mirror the
    // backend's own both-ways tolerance (`_parse_image_keys`) rather than
    // showing the manager an escaped blob.
    const t = value.trim();
    if (t.startsWith('[') || t.startsWith('{')) {
      try {
        return formatSnapshotValue(JSON.parse(t), column);
      } catch {
        return value; // not actually JSON — show it verbatim.
      }
    }
    return value;
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    return value
      .map((v) => (v && typeof v === 'object' ? JSON.stringify(v) : String(v)))
      .join('\n');
  }

  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
}

// ------------------------------------------------------------
// feat-167 fast-follow: mark the fields that actually changed
// ------------------------------------------------------------
//
// A manager reviewing an UPDATE shouldn't read all ~12 fields to find the one
// that moved. We mark the changed rows on BOTH columns — this is a comparison,
// not a claim that the new value is the right one.

/**
 * The changed-row treatment, `ChangedMarker` and `ChangedDot` moved to
 * `./curriculumDiffPresentation` in feat-270 and are imported at the top of this
 * file. They are unchanged; they moved because the approval queue is a second
 * consumer and `design` ruled it reuses them VERBATIM — which is a promise when
 * it is a copy and a fact when it is an import.
 *
 * ⚠ `changedSnapshotFields` and `criteriaChanged` below did NOT move with them,
 * deliberately. They are THIS surface's client-side comparison; the approval
 * surface takes `changed` from the server (ADR-B §2.1) and must not acquire a
 * client twin of that decision. See the header of the new file.
 */

/**
 * Which field keys differ between the two sides, compared on the DISPLAYED
 * string rather than the raw value.
 *
 * Comparing what the manager actually sees is the point: it makes `null` vs `''`
 * (both "—") a non-change, makes 0/1 vs false/true (both "No"/"Yes") a
 * non-change, and makes a JSON column that arrived decoded on one side and as
 * raw text on the other compare equal. Comparing raw values would light up rows
 * whose rendering is identical — a highlight the manager cannot see the reason
 * for is worse than none.
 *
 * Returns an EMPTY set unless both sides exist: on a CREATE or DELETE every
 * field trivially "differs", and the empty-side note already says all-new /
 * all-removed. Highlighting all of them would be noise carrying no information.
 */
export function changedSnapshotFields(
  before: NormalizedSnapshot | null,
  after: NormalizedSnapshot | null,
  fields: string[],
): Set<string> {
  const changed = new Set<string>();
  if (!before || !after) return changed;
  for (const field of fields) {
    if (
      formatSnapshotValue(before.row[field], field) !==
      formatSnapshotValue(after.row[field], field)
    ) {
      changed.add(field);
    }
  }
  return changed;
}

/** Did the rehearsal criteria list change? Compared structurally — order is
 *  meaningful (criteria are position-ordered), so a reorder counts as a change. */
export function criteriaChanged(
  before: NormalizedSnapshot | null,
  after: NormalizedSnapshot | null,
): boolean {
  if (!before || !after) return false;
  return JSON.stringify(before.criteria ?? null) !== JSON.stringify(after.criteria ?? null);
}

/** Did the delete-subtree child counts change? */
export function childCountsChanged(
  before: NormalizedSnapshot | null,
  after: NormalizedSnapshot | null,
): boolean {
  if (!before || !after) return false;
  return JSON.stringify(before.childCounts) !== JSON.stringify(after.childCounts);
}

/** One column of the Before | After pair. `min-w-0` + `break-words` are what
 *  keep a long s3 key or markdown body from widening the slide-over and pushing
 *  the page body into horizontal scroll (AC7). */
/** One field row. Extracted so the authored list and the collapsed system-fields
 *  group share ONE definition of what a changed row looks like. */
const SnapshotField: React.FC<{
  field: string;
  value: unknown;
  changed: boolean;
  muted?: boolean;
}> = ({ field, value, changed, muted }) => (
  <div className={`min-w-0 ${changed ? CHANGED_FIELD_ROW_CLASS : ''}`}>
    <dt className="text-[10px] uppercase tracking-wide text-inkMuted flex items-center gap-1 flex-wrap">
      {changed && <ChangedDot />}
      {snapshotFieldLabel(field)}
      {changed && <ChangedMarker />}
    </dt>
    <dd
      className={`break-words whitespace-pre-wrap ${
        muted ? 'text-[11px] text-inkMuted' : 'text-xs text-ink'
      }`}
    >
      {formatSnapshotValue(value, field)}
    </dd>
  </div>
);

const SnapshotColumn: React.FC<{
  heading: string;
  snapshot: NormalizedSnapshot | null;
  emptyNote: string;
  fields: string[];
  systemFields: string[];
  /** Field keys that differ between the two sides. Empty on CREATE/DELETE. */
  changedFields: Set<string>;
  changedCriteria: boolean;
  changedChildCounts: boolean;
}> = ({
  heading, snapshot, emptyNote, fields, systemFields,
  changedFields, changedCriteria, changedChildCounts,
}) => (
  <div className="min-w-0">
    <div className="text-[10px] font-bold uppercase tracking-wide text-inkMuted mb-1.5">
      {heading}
    </div>
    {snapshot === null ? (
      <div className="text-xs text-inkMuted italic">{emptyNote}</div>
    ) : (
      <dl className="space-y-1.5">
        {fields.map((field) => (
          <SnapshotField
            key={field}
            field={field}
            value={snapshot.row[field]}
            changed={changedFields.has(field)}
          />
        ))}

        {snapshot.criteria && (
          <div className={`min-w-0 pt-0.5 ${changedCriteria ? CHANGED_FIELD_ROW_CLASS : ''}`}>
            <dt className="text-[10px] uppercase tracking-wide text-inkMuted flex items-center gap-1 flex-wrap">
              {changedCriteria && <ChangedDot />}
              Criteria ({snapshot.criteria.length})
              {changedCriteria && <ChangedMarker />}
            </dt>
            <dd className="text-xs text-ink">
              {snapshot.criteria.length === 0 ? (
                <span className="text-inkMuted">—</span>
              ) : (
                <ol className="list-decimal ml-4 space-y-1 mt-0.5">
                  {snapshot.criteria.map((c, i) => (
                    <li key={i} className="break-words">
                      <span className="font-medium">{formatSnapshotValue(c.label)}</span>
                      {c.kind != null && (
                        <span className="text-inkMuted"> · {formatSnapshotValue(c.kind)}</span>
                      )}
                      {c.description != null && String(c.description).trim() !== '' && (
                        <div className="text-inkMuted break-words">
                          {formatSnapshotValue(c.description)}
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </dd>
          </div>
        )}

        {snapshot.childCounts.length > 0 && (
          <div
            className={`min-w-0 pt-0.5 text-[11px] text-inkMuted break-words ${
              changedChildCounts ? CHANGED_FIELD_ROW_CLASS : ''
            }`}
          >
            +{' '}
            {snapshot.childCounts
              .map(({ key, count }) => `${count} ${key.replace(/_/g, ' ')}`)
              .join(', ')}
            {changedChildCounts && (
              <>
                {' '}
                <ChangedDot /> <ChangedMarker />
              </>
            )}
          </div>
        )}

        {/* Demoted, not dropped: identifiers and audit stamps stay auditable
            without burying the fields the manager actually changed (D4).
            Changed system fields are marked here too — `updated` legitimately
            lights on every UPDATE, which is accurate and stays behind the
            disclosure. That accuracy is also why there is no aggregate "N
            changed" badge anywhere: it would be permanently lit. */}
        {systemFields.length > 0 && (
          <details className="min-w-0 pt-1">
            <summary className="text-[10px] uppercase tracking-wide text-inkMuted cursor-pointer">
              System fields
            </summary>
            <div className="mt-1 space-y-1">
              {systemFields.map((field) => (
                <SnapshotField
                  key={field}
                  field={field}
                  value={snapshot.row[field]}
                  changed={changedFields.has(field)}
                  muted
                />
              ))}
            </div>
          </details>
        )}
      </dl>
    )}
  </div>
);

/**
 * The expanded Before | After view for one history entry, plus the
 * "Revert to this version" action.
 *
 * Action-aware framing of the empty side: a CREATE had no before, a DELETE has
 * no after — say so in words rather than rendering an empty column, so the
 * manager reads intent instead of wondering whether the fetch failed.
 */
export const CurriculumChangeDetail: React.FC<{
  entry: CurriculumHistoryEntry;
  snapshot: CurriculumHistorySnapshot | null;
  loading: boolean;
  error: string | null;
  reverting: boolean;
  onRevert: () => void;
}> = ({ entry, snapshot, loading, error, reverting, onRevert }) => {
  const entityType = snapshot?.entity_type ?? entry.entity_type;
  const action = snapshot?.action ?? entry.action;

  const before = useMemo(
    () => (snapshot ? normalizeSnapshot(snapshot.before, entityType) : null),
    [snapshot, entityType],
  );
  const after = useMemo(
    () => (snapshot ? normalizeSnapshot(snapshot.after, entityType) : null),
    [snapshot, entityType],
  );
  const { fields, systemFields } = useMemo(
    () => orderedSnapshotFields(entityType, before?.row, after?.row),
    [entityType, before, after],
  );

  // D5: WHETHER a side is empty is decided by the data (the side is null);
  // `action` only picks the wording. Branching the emptiness itself off `action`
  // would mis-frame a revert, whose sides don't follow the action's usual shape.
  // Which rows to mark. Computed over BOTH the authored and the system field
  // lists, so a changed `updated` marks inside the collapsed group and never
  // bleeds a highlight into the authored list above it.
  const changedFields = useMemo(
    () => changedSnapshotFields(before, after, [...fields, ...systemFields]),
    [before, after, fields, systemFields],
  );
  const changedCriteria = useMemo(() => criteriaChanged(before, after), [before, after]);
  const changedChildCounts = useMemo(() => childCountsChanged(before, after), [before, after]);

  const beforeEmptyNote = action === 'create' ? '— (did not exist)' : '— (no earlier version)';
  const afterEmptyNote = action === 'delete' ? '— (deleted)' : '— (no later version)';

  if (loading) {
    return (
      <div className="mt-2.5 pt-2.5 border-t border-softBorder" data-testid="snapshot-loading">
        <div className="h-3 bg-gray-100 rounded animate-pulse w-1/3 mb-2" />
        <div className="h-16 bg-gray-100 rounded-lg animate-pulse" />
      </div>
    );
  }

  if (error) {
    // Inline and scoped to this row — the list around it stays intact.
    return (
      <div className="mt-2.5 pt-2.5 border-t border-softBorder">
        <div className="text-xs text-danger-text bg-danger-surface border border-danger-line/40 rounded-lg px-2.5 py-2 break-words">
          Couldn’t load this change: {error}
        </div>
      </div>
    );
  }

  if (!snapshot) return null;

  return (
    <div className="mt-2.5 pt-2.5 border-t border-softBorder min-w-0">
      <div className="grid grid-cols-2 gap-3 min-w-0 overflow-x-auto">
        <SnapshotColumn
          heading="Before"
          snapshot={before}
          emptyNote={beforeEmptyNote}
          fields={fields}
          systemFields={systemFields}
          changedFields={changedFields}
          changedCriteria={changedCriteria}
          changedChildCounts={changedChildCounts}
        />
        <SnapshotColumn
          heading="After"
          snapshot={after}
          emptyNote={afterEmptyNote}
          fields={fields}
          systemFields={systemFields}
          changedFields={changedFields}
          changedCriteria={changedCriteria}
          changedChildCounts={changedChildCounts}
        />
      </div>

      {entry.revertable && (
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={onRevert}
            disabled={reverting}
            className="text-xs font-semibold text-coral hover:underline disabled:opacity-40 flex items-center gap-1"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            {reverting
              ? 'Reverting…'
              : action === 'delete'
                ? 'Restore this version'
                : 'Revert to this version'}
          </button>
        </div>
      )}
    </div>
  );
};

export const CurriculumHistoryPanel: React.FC<{
  open: boolean;
  onClose: () => void;
  tenantId: string;
  /** The current selection chain, general → specific (Lesson, Drill, Question|
   *  Rehearsal). Only the selected levels are present. */
  chain: HistoryItemTarget[];
  /** Re-fetch the curriculum tree after a successful revert so the restored/
   *  reverted state shows. */
  onReverted: () => Promise<void>;
}> = ({ open, onClose, tenantId, chain, onReverted }) => {
  // Scopes = each selected item (most-specific first for discoverability) + feed.
  const scopes: HistoryScope[] = useMemo(
    () => [
      ...[...chain].reverse().map((target) => ({ kind: 'item' as const, target })),
      { kind: 'feed' as const },
    ],
    [chain],
  );

  const [activeKey, setActiveKey] = useState<string>('');
  const [entries, setEntries] = useState<CurriculumHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revertingId, setRevertingId] = useState<string | null>(null);

  // feat-167: at most one entry is expanded; its snapshot is fetched lazily and
  // cached per-id so collapsing and re-expanding doesn't refetch. Snapshot
  // errors are keyed per-id (NOT the panel-level `error`) so a failed detail
  // fetch never blows away the list behind it.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<Record<string, CurriculumHistorySnapshot>>({});
  const [snapshotLoadingId, setSnapshotLoadingId] = useState<string | null>(null);
  const [snapshotErrors, setSnapshotErrors] = useState<Record<string, string>>({});

  // Default to the most-specific selected item whenever the panel (re)opens or
  // the selection changes; fall back to the feed when nothing is selected.
  useEffect(() => {
    if (!open) return;
    const stillValid = scopes.some((s) => scopeKey(s) === activeKey);
    if (!stillValid) setActiveKey(scopeKey(scopes[0]));
  }, [open, scopes, activeKey]);

  const activeScope = scopes.find((s) => scopeKey(s) === activeKey) ?? scopes[0];

  const load = useCallback(async () => {
    if (!open || !activeScope) return;
    setLoading(true);
    setError(null);
    // Collapse any open detail — the list underneath is being replaced (scope
    // switch, or a revert that just added an entry). The per-id snapshot cache
    // survives: a history row's stored snapshot is immutable, so re-expanding
    // the same entry is still a cache hit.
    setExpandedId(null);
    try {
      const res =
        activeScope.kind === 'feed'
          ? await getTenantHistory(tenantId, { limit: 100 })
          : await getItemHistory(activeScope.target.entityType, activeScope.target.entityId);
      setEntries(res.entries);
    } catch (e) {
      setEntries([]);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [open, activeScope, tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  // Toggle the Before|After detail for one entry, fetching on first expand.
  const toggleExpanded = useCallback(
    async (entry: CurriculumHistoryEntry) => {
      if (expandedId === entry.id) {
        setExpandedId(null);
        return;
      }
      setExpandedId(entry.id);
      if (snapshots[entry.id]) return; // already cached

      setSnapshotLoadingId(entry.id);
      setSnapshotErrors((prev) => {
        const { [entry.id]: _cleared, ...rest } = prev;
        return rest;
      });
      try {
        const snap = await getHistorySnapshot(entry.id);
        setSnapshots((prev) => ({ ...prev, [entry.id]: snap }));
      } catch (e) {
        setSnapshotErrors((prev) => ({
          ...prev,
          [entry.id]: e instanceof Error ? e.message : String(e),
        }));
      } finally {
        setSnapshotLoadingId(null);
      }
    },
    [expandedId, snapshots],
  );

  const handleRevert = async (entry: CurriculumHistoryEntry) => {
    const label = entityTypeLabel(entry.entity_type);
    const msg =
      entry.action === 'delete'
        ? `Restore this deleted ${label}? It will reappear in place.`
        : `Revert this ${label} to its state before this change? This is itself logged and can be undone.`;
    if (!window.confirm(msg)) return;
    setRevertingId(entry.id);
    setError(null);
    try {
      await revertCurriculumChange(entry.id);
      await onReverted();
      await load(); // a new 'revert' entry now exists — refresh the list.
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setRevertingId(null);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label="Change history">
      <div className="absolute inset-0 bg-ink/30" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-md bg-white h-full shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-softBorder">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2">
            <History className="w-4 h-4" /> Change history
          </h3>
          <button onClick={onClose} className="text-inkMuted hover:text-ink" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scope chips — pick which item's history (or the whole-tenant feed). */}
        <div className="flex flex-wrap gap-1.5 px-5 py-3 border-b border-softBorder">
          {scopes.map((s) => {
            const key = scopeKey(s);
            const active = key === activeKey;
            const text = s.kind === 'feed' ? 'All recent' : s.target.label;
            return (
              <button
                key={key}
                onClick={() => setActiveKey(key)}
                className={`text-xs font-semibold rounded-full px-3 py-1 border ${
                  active
                    ? 'bg-coral text-white border-coral'
                    : 'bg-white text-inkMuted border-softBorder hover:bg-cream'
                }`}
                title={s.kind === 'feed' ? 'All recent changes for this tenant' : `History for this ${entityTypeLabel(s.target.entityType)}`}
              >
                {text}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {error && (
            <div className="mb-3 bg-danger-surface border border-danger-line/40 text-danger-text rounded-xl px-3 py-2 text-sm">
              {error}
            </div>
          )}
          {loading ? (
            <div className="space-y-2">
              <div className="h-14 bg-gray-100 rounded-xl animate-pulse" />
              <div className="h-14 bg-gray-100 rounded-xl animate-pulse" />
              <div className="h-14 bg-gray-100 rounded-xl animate-pulse w-11/12" />
            </div>
          ) : entries.length === 0 && !error ? (
            <div className="text-sm text-inkMuted text-center py-10">
              No changes recorded yet.
            </div>
          ) : (
            <ul className="space-y-2">
              {entries.map((entry) => (
                <li
                  key={entry.id}
                  className="border border-softBorder rounded-xl px-3 py-2.5 min-w-0"
                >
                  <div className="flex items-start gap-3 min-w-0">
                    {/* The whole summary is the expand affordance. The Revert
                        control is a SIBLING, not nested — nested buttons are
                        invalid and would swallow the click. */}
                    <button
                      type="button"
                      onClick={() => toggleExpanded(entry)}
                      aria-expanded={expandedId === entry.id}
                      aria-controls={`history-detail-${entry.id}`}
                      className="flex-1 min-w-0 text-left"
                      title={expandedId === entry.id ? 'Hide what changed' : 'Show what changed'}
                    >
                      <div className="flex items-center gap-2 flex-wrap">
                        {expandedId === entry.id ? (
                          <ChevronDown className="w-3.5 h-3.5 text-inkMuted shrink-0" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5 text-inkMuted shrink-0" />
                        )}
                        <span className={`text-[10px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 ${ACTION_BADGE[entry.action] ?? 'bg-gray-100 text-gray-600'}`}>
                          {entry.action}
                        </span>
                        {activeScope?.kind === 'feed' && (
                          <span className="text-[11px] text-inkMuted font-medium">
                            {entityTypeLabel(entry.entity_type)}
                          </span>
                        )}
                        <span className="text-xs text-inkMuted" title={entry.created_at}>
                          {timeAgo(entry.created_at)}
                        </span>
                      </div>
                      <div className="text-sm text-ink mt-1 break-words">
                        {entry.summary || `${entityTypeLabel(entry.entity_type)} ${entry.action}`}
                      </div>
                      <div className="text-xs text-inkMuted mt-0.5 truncate" title={entry.actor_role === 'system' ? undefined : entry.actor_email}>
                        {actorDisplayName({ email: entry.actor_email, role: entry.actor_role, name: null })}
                      </div>
                    </button>
                    {entry.revertable && (
                      <button
                        onClick={() => handleRevert(entry)}
                        disabled={revertingId != null}
                        className="shrink-0 text-xs font-semibold text-coral hover:underline disabled:opacity-40 flex items-center gap-1"
                        title={entry.action === 'delete' ? 'Restore this item' : 'Revert to before this change'}
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        {revertingId === entry.id ? 'Reverting…' : entry.action === 'delete' ? 'Restore' : 'Revert'}
                      </button>
                    )}
                  </div>

                  {expandedId === entry.id && (
                    <div id={`history-detail-${entry.id}`} className="min-w-0">
                      <CurriculumChangeDetail
                        entry={entry}
                        snapshot={snapshots[entry.id] ?? null}
                        loading={snapshotLoadingId === entry.id}
                        error={snapshotErrors[entry.id] ?? null}
                        reverting={revertingId === entry.id}
                        onRevert={() => handleRevert(entry)}
                      />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

interface CurriculumEditorProps {
  tenantId: string;
  content: QuizContent | null;
  loading: boolean;
  error: string | null;
  availableRoles: AvailableRoles;
  onReload: () => Promise<void>;
  renderImporter?: ImporterRenderer;
  // feat-235 — the tracks payload rides alongside the curriculum. It is loaded
  // and FAILS separately on purpose: a tenant whose backend predates migration
  // 114 still gets a working Lessons tab.
  tracks?: LearnTrack[];
  unassignedLessonIds?: string[];
  tracksLoading?: boolean;
  tracksError?: string | null;
  onReloadTracks?: () => Promise<void>;
  /** D2 health for this tenant. Drives the alert AND the suppression of track
   *  creation on `missing_no_tracks` — see `DefaultTrackAlert`. */
  defaultTrackState?: DefaultTrackState;
  /**
   * feat-270 AT-25 / AT-29. `null` means the approval surface is ABSENT — either
   * the tenant flag is off, or the summary has not loaded / failed to load. In
   * every one of those cases the editor renders exactly as it does today: no
   * badge, no entry point, no per-lesson chip.
   *
   * Absence rather than zeros is deliberate (§10.10): a `0` asserts a
   * measurement, and a failed fetch has not taken one.
   */
  approval?: {
    byLessonId: ReadonlyMap<string, LessonApprovalRow>;
    lessonsPending: number;
    changesPending: number;
  } | null;
}

const CurriculumEditor: React.FC<CurriculumEditorProps> = ({
  tenantId,
  content,
  loading,
  error,
  availableRoles,
  onReload,
  renderImporter,
  tracks,
  unassignedLessonIds = [],
  tracksLoading = false,
  tracksError = null,
  onReloadTracks,
  approval = null,
  defaultTrackState = 'ok',
}) => {
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const [selectedDrillId, setSelectedDrillId] = useState<string | null>(null);
  const [expandedUnitIds, setExpandedUnitIds] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [sel, setSel] = useState<Selection>({ kind: 'none' });
  // feat-164: change-history slide-over (per-item history + tenant feed).
  const [historyOpen, setHistoryOpen] = useState(false);
  // feat-235: which tab the card is showing, and the Tracks tab's selection.
  // Both live HERE rather than inside TracksTab because the app bar's primary
  // action and the shared History panel's item chain both depend on them.
  const [activeTab, setActiveTab] = useState<'lessons' | 'tracks'>('lessons');
  const [selectedTrackId, setSelectedTrackId] = useState<string | null>(null);

  // feat-259 AC10 — an auto-save patches the item's row IN PLACE. It must not
  // call `onReload()`: that refetches the whole drill and re-renders the middle
  // column and the panel, which on every blur is a re-render storm and, more to
  // the point, A FOCUS THIEF — the field the editor is still typing in loses
  // focus mid-sentence. Create and delete DO still reload, because both change
  // which rows exist.
  //
  // `content` is a prop, so the patch lives here as an overlay keyed
  // `"<kind>:<id>"`. It is dropped whenever a real reload lands, so the server's
  // copy always wins in the end.
  //
  // The overlay is laid over the WHOLE unit list, once, here — `qa` Finding 8.
  // `units` is the only tree any consumer reads, so the drill pill and the
  // lesson pill cannot see different content. See `rowPatches.ts`.
  const [rowPatches, setRowPatches] = useState<Record<string, Record<string, unknown>>>({});
  useEffect(() => setRowPatches({}), [content]);

  const patchItemRow = useCallback((kind: DrillItem['kind'], id: string, changed: Record<string, unknown>) => {
    setRowPatches((prev) => ({ ...prev, [`${kind}:${id}`]: { ...prev[`${kind}:${id}`], ...changed } }));
  }, []);

  const units = useMemo(() => applyRowPatches(content?.lessons ?? [], rowPatches), [content, rowPatches]);
  const trackList = useMemo(() => tracks ?? [], [tracks]);
  const selectedTrack = trackList.find((t) => t.id === selectedTrackId) ?? null;

  // Initialize / repair selection when content loads or the selected unit vanishes.
  useEffect(() => {
    if (units.length === 0) return;
    if (selectedUnitId && units.some((u) => u.id === selectedUnitId)) return;
    const first = units[0];
    setSelectedUnitId(first.id);
    setExpandedUnitIds(new Set([first.id]));
    const d = first.drills[0];
    setSelectedDrillId(d ? d.id : null);
    setSel(d ? firstItemSelection(d) : { kind: 'none' });
  }, [units, selectedUnitId]);

  // Both of these come off the PATCHED `units`, so every consumer below —
  // including both publish-guard call sites — reads the same tree.
  const selectedUnit = units.find((u) => u.id === selectedUnitId) ?? null;

  // ── feat-270 panels 3 / 4 / 9 — the detail panel's approval state ──────────
  //
  // ONE fetch per selected Lesson (ADR-B §3.3), not one per item: the response
  // carries every pending item in the Lesson with its `fields`, so switching
  // between items in the same Lesson costs nothing.
  //
  // `undefined` is "not loaded", `null` is "the fetch FAILED". They must stay
  // distinguishable, because §10.10 makes the absence of a marker a CLAIM — a
  // failed fetch that renders no markers is indistinguishable from "everything
  // matches", and would tell an editor their draft is live when it may not be.
  const gate = useApprovalGate();
  const [lessonApproval, setLessonApproval] = useState<
    ApprovalLessonPage | null | undefined
  >(undefined);
  const [approvalNonce, setApprovalNonce] = useState(0);

  useEffect(() => {
    if (!gate.approvalRequired || !selectedUnitId) {
      setLessonApproval(undefined);
      return;
    }
    let cancelled = false;
    setLessonApproval(undefined);
    void (async () => {
      try {
        const detail = await getApprovalLessonDetail(tenantId, selectedUnitId);
        if (!cancelled) setLessonApproval(detail);
      } catch {
        if (!cancelled) setLessonApproval(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [gate.approvalRequired, selectedUnitId, tenantId, approvalNonce]);

  /** The entity currently in the detail panel, whatever its type. */
  const selectedEntityId =
    sel.kind === 'question' || sel.kind === 'rehearsal' || sel.kind === 'video' || sel.kind === 'reading'
      ? sel.id
      : null;

  const itemApproval = useMemo<ItemApprovalState>(() => {
    if (!gate.approvalRequired || !selectedUnitId) {
      return { status: 'off', lessonState: null, lessonTitle: null, item: null };
    }
    if (lessonApproval === null) {
      return {
        status: 'error',
        lessonState: null,
        lessonTitle: selectedUnit?.title ?? null,
        item: null,
        onRetry: () => setApprovalNonce((n) => n + 1),
      };
    }
    if (lessonApproval === undefined) {
      return { status: 'loading', lessonState: null, lessonTitle: null, item: null };
    }
    return {
      status: 'ready',
      lessonState: lessonApproval.state,
      lessonTitle: lessonApproval.title,
      item:
        (selectedEntityId
          ? lessonApproval.items.find((i) => i.entity_ref.entity_id === selectedEntityId)
          : undefined) ?? null,
    };
  }, [gate.approvalRequired, selectedUnitId, lessonApproval, selectedEntityId, selectedUnit]);
  const selectedDrill = selectedUnit?.drills.find((l) => l.id === selectedDrillId) ?? null;

  // feat-259 AC9 — the open panel registers its `flush` here. Selecting another
  // item runs it and only then changes `sel`. That ordering matters twice over:
  // the pending write must land BEFORE the row it belongs to stops being
  // selected, and the panels carry `key` props that force a REMOUNT on
  // selection change — so the flush has to happen before the remount, never
  // racing it.
  const flushPanelRef = useRef<(() => Promise<boolean>) | null>(null);
  const registerPanelFlush = useCallback((fn: (() => Promise<boolean>) | null) => {
    flushPanelRef.current = fn;
  }, []);
  const [heldSelection, setHeldSelection] = useState<Selection | null>(null);

  const requestSel = useCallback(async (next: Selection) => {
    const flush = flushPanelRef.current;
    if (flush) {
      const ok = await flush();
      if (!ok) {
        // The write did not land. The selection does NOT change; the panel's own
        // banner escalates in place and offers Try again / Leave anyway.
        setHeldSelection(next);
        return;
      }
    }
    setHeldSelection(null);
    setSel(next);
  }, []);

  /** Both arms of AT9 end here: a retry that landed, and an explicit
   *  "Leave anyway" that abandons the change. The escape hatch is what keeps an
   *  editor from being trapped in one item by a save that can never succeed —
   *  and a page reload, the only other way out, would discard the change
   *  SILENTLY, which is exactly what AT8 forbids. */
  const proceedWithHeldSelection = useCallback(() => {
    setHeldSelection((held) => {
      if (held) {
        flushPanelRef.current = null;
        setSel(held);
      }
      return null;
    });
  }, []);

  const items: DrillItem[] = selectedDrill ? (interleaveItems(selectedDrill) as DrillItem[]) : [];
  const nextSort =
    items.reduce(
      (m, it) =>
        Math.max(
          m,
          it.kind === 'question'
            ? it.q.sort_order
            : it.kind === 'rehearsal'
              ? it.r.sort_order
              : it.kind === 'video'
                ? it.v.sort_order
                : it.rd.sort_order,
        ),
      0,
    ) + 1;

  const selectedItemKey =
    sel.kind === 'question'
      ? `question:${sel.id}`
      : sel.kind === 'rehearsal'
        ? `rehearsal:${sel.id}`
        : sel.kind === 'video'
          ? `video:${sel.id}`
          : sel.kind === 'reading'
            ? `reading:${sel.id}`
            : null;

  const importerCtx: ImporterContext = {
    tenantId,
    units,
    selectedUnitId,
    onImported: onReload,
  };

  // --- tree handlers ---
  const toggleUnit = (unitId: string) =>
    setExpandedUnitIds((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) next.delete(unitId);
      else next.add(unitId);
      return next;
    });

  const selectDrill = (unitId: string, drill: QuizLesson) => {
    setSelectedUnitId(unitId);
    setSelectedDrillId(drill.id);
    setExpandedUnitIds((prev) => new Set(prev).add(unitId));
    setSel(firstItemSelection(drill));
  };

  // feat-354 (design NOTES §2b) — the "Copy of" link: the same jump as the Tracks
  // map's onOpenLesson, staying on the Lessons tab. Clears drill + item selection,
  // expands the original, and clears a tree search that would hide it.
  const openOriginalLesson = (lessonId: string) => {
    const original = units.find((u) => u.id === lessonId);
    if (!original) return;
    const q = search.trim().toLowerCase();
    const hit = (t: string) => t.toLowerCase().includes(q);
    if (q && !(hit(original.title) || original.drills.some((d) => hit(d.title)))) setSearch('');
    setSelectedUnitId(lessonId);
    setSelectedDrillId(null);
    setSel({ kind: 'none' });
    setExpandedUnitIds((prev) => new Set(prev).add(lessonId));
  };

  const createLessonHandler = async (title: string) => {
    const { id } = await createUnit(tenantId, { title, sort_order: units.length + 1 });
    await onReload();
    setSelectedUnitId(id);
    setExpandedUnitIds(new Set([id]));
    setSelectedDrillId(null);
    setSel({ kind: 'none' });
  };

  // feat-235 — used by BOTH the app-bar primary action and the Tracks pane's
  // footer button, so a track created either way lands selected and at the end.
  const createTrackHandler = async (title: string) => {
    const { id } = await createTrack(tenantId, { title, sort_order: trackList.length + 1 });
    if (onReloadTracks) await onReloadTracks();
    setActiveTab('tracks');
    setSelectedTrackId(id);
  };

  const createDrillHandler = async (unitId: string, title: string) => {
    const unit = units.find((u) => u.id === unitId);
    const sort = (unit?.drills.length ?? 0) + 1;
    const { id } = await createLesson(unitId, { title, sort_order: sort });
    await onReload();
    setSelectedUnitId(unitId);
    setExpandedUnitIds((prev) => new Set(prev).add(unitId));
    setSelectedDrillId(id);
    setSel({ kind: 'none' });
  };

  const reorderUnits = async (orderedIds: string[]) => {
    await Promise.all(orderedIds.map((id, i) => updateUnit(id, { sort_order: i + 1 })));
    await onReload();
  };
  const reorderDrills = async (unitId: string, orderedIds: string[]) => {
    await Promise.all(orderedIds.map((id, i) => updateLesson(id, { sort_order: i + 1 })));
    await onReload();
  };

  // feat-259 AC13 / AT14 — the publish-time empty guard. ONE gate, reached from
  // both live toggle sites, because both pills already funnel through the meta
  // savers below (`LessonMetaPanel`'s `onSave`, `DrillChildList`'s
  // `onSaveDrillMeta`). Guarding HERE rather than at each pill is what stops
  // this shipping with the lesson guarded and the drill not.
  const [publishGuard, setPublishGuard] = useState<{
    title: string;
    report: PublishGuardReport;
    resolve: (proceed: boolean) => void;
  } | null>(null);

  /** Resolves false only when the editor dismisses the dialog. No dialog ⇒ true,
   *  which covers BOTH negative cases in AT14: nothing empty, and no items at
   *  all. Turning something OFF is never guarded. */
  const confirmPublish = useCallback((title: string, report: PublishGuardReport): Promise<boolean> => {
    if (!needsPublishConfirmation(report)) return Promise.resolve(true);
    return new Promise<boolean>((resolve) => setPublishGuard({ title, report, resolve }));
  }, []);

  const closePublishGuard = useCallback((proceed: boolean) => {
    setPublishGuard((g) => {
      g?.resolve(proceed);
      return null;
    });
  }, []);

  // --- center (Drill meta + child) handlers ---
  const saveDrillMeta = async (patch: DrillMetaPatch) => {
    if (!selectedDrill) return;
    // Only the Hidden→Active transition is intercepted. The pill still reads
    // Hidden while the dialog is open, because it still IS — the state changes
    // when the editor confirms, not when they click.
    if (patch.active === true && !(await confirmPublish(selectedDrill.title, reportForDrill(selectedDrill)))) {
      return;
    }
    await updateLesson(selectedDrill.id, patch);
    await onReload();
  };
  // ── feat-361 — Move a Drill to another Lesson (design/NOTES.md §4) ─────────
  //
  // NO optimistic update: the tree changes only after the move is confirmed and
  // the reload lands, so an error leaves nothing to roll back.
  // Captured at open so the dialog never depends on the selection, which is
  // briefly in flux while the post-move reload lands.
  const [moveDialog, setMoveDialog] = useState<{ drillId: string; drillTitle: string; lessonId: string } | null>(null);
  const [moveNotice, setMoveNotice] = useState<{ lessonTitle: string } | null>(null);
  // Set once the server confirms the move; resolved below when the reloaded
  // tree actually contains the drill under its new Lesson. A failed reload
  // leaves it pending, so the selection never points at a Lesson the drill is
  // not (yet) shown in.
  const [pendingMove, setPendingMove] = useState<{ drillId: string; targetId: string } | null>(null);

  // Layout effect, not a plain effect: the reload commits a render in which the
  // drill has already left the source Lesson; re-pointing the selection before
  // paint keeps that frame from ever reaching the screen.
  useLayoutEffect(() => {
    if (!pendingMove) return;
    const target = units.find((u) => u.id === pendingMove.targetId);
    if (!target?.drills.some((d) => d.id === pendingMove.drillId)) return;
    setSelectedUnitId(pendingMove.targetId);
    setSelectedDrillId(pendingMove.drillId);
    setExpandedUnitIds((prev) => new Set(prev).add(pendingMove.targetId));
    setPendingMove(null);
  }, [units, pendingMove]);

  // The strip clears when the author selects a different drill or a lesson
  // (the crumb nulls the drill id). A move keeps the same id, so it survives.
  useEffect(() => {
    setMoveNotice(null);
  }, [selectedDrillId]);

  const openMoveDialog = () => {
    if (!selectedDrill || !selectedUnit) return;
    setMoveNotice(null); // "starting another move" clears the strip (§5)
    setMoveDialog({ drillId: selectedDrill.id, drillTitle: selectedDrill.title, lessonId: selectedUnit.id });
  };

  /** Rejects on failure — MoveDrillDialog shows the error and stays open. */
  const moveDrillHandler = async (targetLessonId: string) => {
    if (!moveDialog) return;
    const drill = { id: moveDialog.drillId, title: moveDialog.drillTitle };
    const target = units.find((u) => u.id === targetLessonId);
    // The item panel unmounts while the pane re-renders: land any pending
    // autosave first rather than lose it. A failed flush aborts the move.
    const flush = flushPanelRef.current;
    if (flush && !(await flush())) throw new Error('flush failed');
    await moveDrill(drill.id, targetLessonId);
    setExpandedUnitIds((prev) => new Set(prev).add(targetLessonId));
    // A tree search that would hide the moved row in its new Lesson is cleared;
    // a selected row the author can't see is worse than a cleared filter.
    const q = search.trim().toLowerCase();
    if (q && target) {
      const hit = (t: string) => (t || '').toLowerCase().includes(q);
      if (!hit(drill.title) && !hit(target.title)) setSearch('');
    }
    setPendingMove({ drillId: drill.id, targetId: targetLessonId });
    await onReload();
    setMoveDialog(null);
    setMoveNotice({ lessonTitle: target?.title ?? '' });
  };

  const deleteDrill = async () => {
    if (!selectedDrill) return;
    if (!window.confirm(`Delete ${QUIZ_TERMS.drill.singular} “${selectedDrill.title}”? This removes its ${QUIZ_TERMS.question.plural} & ${QUIZ_TERMS.rehearsal.plural}.`)) return;
    await deleteLesson(selectedDrill.id);
    setSelectedDrillId(null);
    setSel({ kind: 'none' });
    await onReload();
  };
  const reorderItems = async (orderedKeys: string[]) => {
    await Promise.all(
      orderedKeys.map((key, i) => {
        const [kind, id] = key.split(':');
        if (kind === 'question') return updateQuestion(id, { sort_order: i + 1 });
        if (kind === 'rehearsal') return updateRehearsal(id, { sort_order: i + 1 });
        if (kind === 'video') return updateVideoItem(id, { sort_order: i + 1 }); // feat-165
        return updateReadingItem(id, { sort_order: i + 1 }); // feat-166
      }),
    );
    await onReload();
  };

  const saveLessonMeta = async (patch: Partial<{ title: string; subtitle: string; mascot_key: string | null; image_s3_key: string | null; emoji: string | null; active: boolean; available_at: string | null; drip_bypass: boolean }>) => {
    if (!selectedUnit) return;
    if (patch.active === true && !(await confirmPublish(selectedUnit.title, reportForUnit(selectedUnit)))) {
      return;
    }
    await updateUnit(selectedUnit.id, patch);
    await onReload();
  };
  const deleteLessonHandler = async () => {
    if (!selectedUnit) return;
    if (!window.confirm(`Delete ${QUIZ_TERMS.lesson.singular} “${selectedUnit.title}”? This removes all its ${QUIZ_TERMS.drill.plural}.`)) return;
    await deleteUnit(selectedUnit.id);
    setSelectedUnitId(null);
    setSelectedDrillId(null);
    setSel({ kind: 'none' });
    await onReload();
  };

  // --- right pane ---
  const selectedQuestion =
    sel.kind === 'question' ? selectedDrill?.question_items.find((q) => q.id === sel.id) ?? null : null;
  const selectedRehearsal =
    sel.kind === 'rehearsal' ? selectedDrill?.rehearsal_items.find((r) => r.id === sel.id) ?? null : null;
  const selectedVideo: VideoItemSummary | null =
    sel.kind === 'video' ? selectedDrill?.video_items?.find((v) => v.id === sel.id) ?? null : null;
  const selectedReading: ReadingItemSummary | null =
    sel.kind === 'reading' ? selectedDrill?.reading_items?.find((rd) => rd.id === sel.id) ?? null : null;

  // feat-164: the selected item chain (general → specific) the history panel
  // exposes. Labels use the feat-107 UI terms; entityType is the DB name.
  const historyChain: HistoryItemTarget[] = useMemo(() => {
    const chain: HistoryItemTarget[] = [];
    // feat-235 — History is SHARED between the tabs (mockup §1): track edits
    // capture to `curriculum_change_history` like everything else, so they
    // belong in the same panel. They are capture-only — the backend lists the
    // three track entity types as non-revertable, and the panel already renders
    // an unrevertable entry as view-only.
    if (activeTab === 'tracks' && selectedTrack) {
      chain.push({
        entityType: CURRICULUM_ENTITY_TYPE.track,
        entityId: selectedTrack.id,
        label: `${QUIZ_TERMS.track.singular}: ${selectedTrack.title || '(untitled)'}`,
      });
      return chain;
    }
    if (selectedUnit) {
      chain.push({
        entityType: CURRICULUM_ENTITY_TYPE.unit,
        entityId: selectedUnit.id,
        label: `${QUIZ_TERMS.lesson.singular}: ${selectedUnit.title || '(untitled)'}`,
      });
    }
    if (selectedDrill) {
      chain.push({
        entityType: CURRICULUM_ENTITY_TYPE.lesson,
        entityId: selectedDrill.id,
        label: `${QUIZ_TERMS.drill.singular}: ${selectedDrill.title || '(untitled)'}`,
      });
    }
    if (selectedQuestion) {
      chain.push({
        entityType: CURRICULUM_ENTITY_TYPE.question,
        entityId: selectedQuestion.id,
        label: `${QUIZ_TERMS.question.singular}`,
      });
    } else if (selectedRehearsal) {
      chain.push({
        entityType: CURRICULUM_ENTITY_TYPE.rehearsal,
        entityId: selectedRehearsal.id,
        label: `${QUIZ_TERMS.rehearsal.singular}: ${selectedRehearsal.name || '(unnamed)'}`,
      });
    } else if (selectedVideo) {
      chain.push({
        entityType: CURRICULUM_ENTITY_TYPE.video,
        entityId: selectedVideo.id,
        label: `${QUIZ_TERMS.video.singular}: ${selectedVideo.title || '(untitled)'}`,
      });
    } else if (selectedReading) {
      chain.push({
        entityType: CURRICULUM_ENTITY_TYPE.reading,
        entityId: selectedReading.id,
        label: `${QUIZ_TERMS.reading.singular}: ${selectedReading.title || '(untitled)'}`,
      });
    }
    return chain;
  }, [activeTab, selectedTrack, selectedUnit, selectedDrill, selectedQuestion, selectedRehearsal, selectedVideo, selectedReading]);

  // feat-109: the detail-pane Type control picks the concrete type for a NEW item.
  const requestDetailType = (next: DetailItemType) => setSel({ kind: 'new', itemType: next });

  const renderRightPane = () => {
    if (!selectedDrill) return null;
    const lessonId = selectedDrill.id;
    const isNew = sel.kind === 'new';
    const newIsRehearsal = sel.kind === 'new' && sel.itemType === 'rehearsal';
    const newIsVideo = sel.kind === 'new' && sel.itemType === 'video';
    const newIsReading = sel.kind === 'new' && sel.itemType === 'reading';
    if (sel.kind === 'question' || (isNew && !newIsRehearsal && !newIsVideo && !newIsReading)) {
      const initialType: QuestionType =
        sel.kind === 'new' && sel.itemType === 'fill_blank' ? 'fill_blank' : 'multiple_choice';
      return (
        <QuestionDetailForm
          key={sel.kind === 'question' ? sel.id : `new-question-${initialType}`}
          question={sel.kind === 'question' ? selectedQuestion : null}
          lessonId={lessonId}
          defaultSort={nextSort}
          initialType={initialType}
          onRequestRehearsal={sel.kind === 'new' ? () => requestDetailType('rehearsal') : undefined}
          onRequestVideo={sel.kind === 'new' ? () => requestDetailType('video') : undefined}
          onRequestReading={sel.kind === 'new' ? () => requestDetailType('reading') : undefined}
          // A create changes which rows exist, so it still reloads (AC10).
          onCreated={async (savedId) => {
            await onReload();
            setSel({ kind: 'question', id: savedId });
          }}
          // An auto-save does NOT. The row label updates from the patch.
          onPatched={(id, changed) => patchItemRow('question', id, changed)}
          registerFlush={registerPanelFlush}
          pendingNavigation={heldSelection !== null}
          onNavigationProceed={proceedWithHeldSelection}
          onDelete={
            sel.kind === 'question'
              ? async () => {
                  await onReload();
                  setSel(firstItemSelection(selectedDrill));
                }
              : undefined
          }
        />
      );
    }
    if (sel.kind === 'rehearsal' || newIsRehearsal) {
      const existingId = sel.kind === 'rehearsal' ? sel.id : null;
      return (
        // feat-231: `pb-0` pairs with the RehearsalEditor's `sticky bottom-0`
        // action row. A sticky bottom offset resolves against the scroll
        // container's CONTENT box, so with the pane's usual `p-4` the pinned row
        // parks 16px above the pane floor and scrolling fields show through the
        // strip underneath it (measured in Chrome: 16px, "Correct example
        // response" visible below the Save button). The editor's own `p-3`
        // still supplies the inner padding.
        <aside className="border-l border-softBorder bg-cream/40 min-h-0 overflow-y-auto p-4 pb-0 space-y-4">
          {/* feat-109: 3-way Type control for a NEW item so the admin can switch
              back to a Question form; existing rehearsals have no type switch. */}
          {existingId === null && (
            <DetailTypeControl value="rehearsal" onChange={requestDetailType} />
          )}
          <RehearsalEditor
            key={existingId ?? 'new-rehearsal'}
            tenantId={tenantId}
            lessonId={lessonId}
            existingId={existingId}
            pinnedVersionNumber={selectedRehearsal?.prompt_template_version_number ?? null}
            availableRoles={availableRoles}
            initial={selectedRehearsal ? rehearsalToDraft(selectedRehearsal) : emptyRehearsalDraft(nextSort, availableRoles ?? [])}
            created={selectedRehearsal?.created}
            updated={selectedRehearsal?.updated}
            onCreated={async (savedId) => {
              await onReload();
              setSel({ kind: 'rehearsal', id: savedId });
            }}
            onPatched={(id, changed) => patchItemRow('rehearsal', id, changed)}
            registerFlush={registerPanelFlush}
            pendingNavigation={heldSelection !== null}
            onNavigationProceed={proceedWithHeldSelection}
            onDelete={
              existingId
                ? async () => {
                    // design/NOTES.md §4 advisory — the button says Delete now,
                    // so its own dialog stops disagreeing with it.
                    if (!window.confirm(`Delete this ${QUIZ_TERMS.rehearsal.singular}?`)) return;
                    await deleteRehearsal(existingId);
                    await onReload();
                    setSel(firstItemSelection(selectedDrill));
                  }
                : undefined
            }
          />
        </aside>
      );
    }
    // feat-165: explanatory video item detail pane.
    // feat-259: the `<aside>` moved INTO VideoDetailForm — see the reading pane
    // above for why a footer cannot be a `shrink-0` sibling of a scroll area it
    // is nested inside.
    if (sel.kind === 'video' || newIsVideo) {
      const existingId = sel.kind === 'video' ? sel.id : null;
      return (
        <VideoDetailForm
          key={existingId ?? 'new-video'}
          tenantId={tenantId}
          lessonId={lessonId}
          existingId={existingId}
          summary={selectedVideo}
          defaultSort={nextSort}
          typeControl={
            existingId === null ? <DetailTypeControl value="video" onChange={requestDetailType} /> : undefined
          }
          // AC12 — the COMPLETED UPLOAD creates the item, exactly once.
          onCreated={async (savedId) => {
            await onReload();
            setSel({ kind: 'video', id: savedId });
          }}
          onPatched={(id, changed) => patchItemRow('video', id, changed)}
          // Publish / caption retry / source replace genuinely change the item
          // beyond its row label, so those DO reload. A title blur does not.
          onReloadNeeded={() => void onReload()}
          registerFlush={registerPanelFlush}
          pendingNavigation={heldSelection !== null}
          onNavigationProceed={proceedWithHeldSelection}
          onDelete={
            existingId
              ? async () => {
                  if (!window.confirm(`Delete this ${QUIZ_TERMS.video.singular}?`)) return;
                  await deleteVideoItem(existingId);
                  await onReload();
                  setSel(firstItemSelection(selectedDrill));
                }
              : undefined
          }
        />
      );
    }
    // feat-166: reading item detail pane.
    // feat-259: the `<aside>` moved INTO ReadingDetailForm — it needs a real
    // scroll-area / footer split (design/NOTES.md §3), and a footer cannot be a
    // `shrink-0` sibling of a scroll area it is nested inside. The type control
    // rides along as a prop so it stays at the top of the SCROLL child.
    if (sel.kind === 'reading' || newIsReading) {
      const existingId = sel.kind === 'reading' ? sel.id : null;
      return (
        <ReadingDetailForm
          key={existingId ?? 'new-reading'}
          tenantId={tenantId}
          lessonId={lessonId}
          existingId={existingId}
          summary={selectedReading}
          defaultSort={nextSort}
          typeControl={
            existingId === null ? <DetailTypeControl value="reading" onChange={requestDetailType} /> : undefined
          }
          onCreated={async (savedId) => {
            await onReload();
            setSel({ kind: 'reading', id: savedId });
          }}
          onPatched={(id, changed) => patchItemRow('reading', id, changed)}
          registerFlush={registerPanelFlush}
          pendingNavigation={heldSelection !== null}
          onNavigationProceed={proceedWithHeldSelection}
          onDelete={
            existingId
              ? async () => {
                  if (!window.confirm(`Delete this ${QUIZ_TERMS.reading.singular}?`)) return;
                  await deleteReadingItem(existingId);
                  await onReload();
                  setSel(firstItemSelection(selectedDrill));
                }
              : undefined
          }
        />
      );
    }
    return (
      <aside className="border-l border-softBorder bg-cream/40 min-h-0 overflow-y-auto flex items-center justify-center p-6 text-center">
        <p className="text-sm text-inkMuted">Select a {QUIZ_TERMS.question.singular}, {QUIZ_TERMS.rehearsal.singular}, {QUIZ_TERMS.video.singular}, or {QUIZ_TERMS.reading.singular} to edit, or add one below.</p>
      </aside>
    );
  };

  // --- body ---
  let body: React.ReactNode;
  if (loading && !content) {
    body = <ThreePaneSkeleton />;
  } else if (error && !content) {
    body = (
      // feat-231: `flex-1` so the card keeps its bounded shape while the error
      // state is showing; `overflow-y-auto` so a long message scrolls in place
      // rather than pushing the card past the viewport.
      <div className="p-6 flex-1 min-h-0 overflow-y-auto">
        <div className="bg-danger-surface border border-danger-line/40 text-danger-text rounded-2xl px-5 py-4 flex items-center gap-4">
          <span className="text-2xl">⚠️</span>
          <div className="flex-1">
            <h4 className="font-display font-semibold">Couldn't load the Learn Module</h4>
            <p className="text-sm opacity-90">
              The request to <code className="bg-white/60 rounded px-1">/api/tenant/…/learn-content</code> failed. Your unsaved edits are kept.
            </p>
          </div>
          <button onClick={onReload} className="bg-danger-text text-white font-display font-semibold rounded-full px-4 py-2 text-sm hover:brightness-110 shrink-0">
            ↻ Retry
          </button>
        </div>
      </div>
    );
  } else if (units.length === 0) {
    body = (
      <div className="p-10 flex-1 min-h-0 overflow-y-auto flex justify-center">
        <div className="bg-white border border-softBorder rounded-3xl shadow-sm p-8 text-center max-w-md">
          <div className="text-5xl mb-3">📚</div>
          <h4 className="font-display text-lg font-semibold">No {QUIZ_TERMS.lesson.plural} yet</h4>
          <p className="text-sm text-inkMuted mt-1 mb-4">
            Build your first {QUIZ_TERMS.lesson.singular}, or paste your Notion outline to scaffold the whole tree.
          </p>
          <div className="flex flex-col gap-2 items-stretch">
            <button
              onClick={() => createLessonHandler('New ' + QUIZ_TERMS.lesson.singular)}
              className="bg-coral text-white font-display font-semibold rounded-full px-4 py-2 text-sm hover:brightness-95"
            >
              + Create your first {QUIZ_TERMS.lesson.singular}
            </button>
            {renderImporter?.(importerCtx, 'empty')}
          </div>
        </div>
      </div>
    );
  } else {
    body = (
      <>
        {error && content && (
          <div className="mx-5 mt-4 shrink-0 bg-danger-surface border border-danger-line/40 text-danger-text rounded-xl px-4 py-2 text-sm flex items-center justify-between gap-3">
            <span>Refresh failed — showing the last loaded content. Unsaved edits are kept.</span>
            <button onClick={onReload} className="font-semibold hover:underline shrink-0">↻ Retry</button>
          </div>
        )}
        {/* feat-231: was `style={{…, minHeight: 640}}` with no bounded height —
            a floor and no ceiling, so the row grew to fit 45 lessons and every
            `flex-1 overflow-y-auto` inside it had nothing to overflow.
            `flex-1` takes the height the card hands down; the explicit
            `minmax(0, 1fr)` row lets the row shrink below its items' content;
            `minHeight: 480` is the short-window pane floor from the spec.

            `contain: 'size'` is NOT optional and NOT a performance tweak —
            measured in Chrome, removing it puts the whole bug back. The card
            above is `min-h-min`, and without size containment this grid's
            min-content height is the panes' FULL content height (2166px for 45
            lessons), so the card's min-content is 2255px, the card grows to it,
            and the page scrolls again — the `minmax(0, 1fr)` row does not stop
            that on its own. `contain: size` is the one declaration that says
            "my height does not depend on my contents", which is exactly true
            here: all three panes are their own scroll containers. Safe because
            this box can never be auto-height — it is always a `flex-1` item
            with a 480px floor, so size containment can never collapse it. */}
        <div
          className="grid flex-1"
          style={{
            gridTemplateColumns: '280px minmax(0,1fr) 380px',
            gridTemplateRows: 'minmax(0, 1fr)',
            minHeight: 480,
            contain: 'size',
          }}
        >
          <MasterDetailTree
            units={units}
            search={search}
            onSearch={setSearch}
            expandedUnitIds={expandedUnitIds}
            onToggleUnit={toggleUnit}
            selectedDrillId={selectedDrillId}
            onSelectDrill={selectDrill}
            glyphForKey={glyphForKey}
            onCreateLesson={createLessonHandler}
            onCreateDrill={createDrillHandler}
            onReorderUnits={reorderUnits}
            onReorderDrills={reorderDrills}
            approvalByLessonId={approval?.byLessonId ?? null}
          />

          {selectedDrill ? (
            <DrillChildList
              unit={selectedUnit!}
              drill={selectedDrill}
              items={items}
              selectedItemKey={selectedItemKey}
              onSelectItem={(item) =>
                // AC9 — flush first; `requestSel` holds the change if it fails.
                void requestSel(
                  item.kind === 'question'
                    ? { kind: 'question', id: item.q.id }
                    : item.kind === 'rehearsal'
                      ? { kind: 'rehearsal', id: item.r.id }
                      : item.kind === 'video'
                        ? { kind: 'video', id: item.v.id }
                        : { kind: 'reading', id: item.rd.id },
                )
              }
              onSelectLessonCrumb={() => {
                setSelectedDrillId(null);
                setSel({ kind: 'none' });
              }}
              glyphForKey={glyphForKey}
              onSaveDrillMeta={saveDrillMeta}
              onDeleteDrill={deleteDrill}
              onAdd={() => void requestSel({ kind: 'new', itemType: 'multiple_choice' })}
              onReorderItems={reorderItems}
              onMoveDrill={openMoveDialog}
              moveNotice={moveNotice}
              onDismissMoveNotice={() => setMoveNotice(null)}
            />
          ) : selectedUnit ? (
            <LessonMetaPanel
              approvalRow={approval?.byLessonId.get(selectedUnit.id) ?? null}
              unit={selectedUnit}
              tenantId={tenantId}
              onSave={saveLessonMeta}
              onDelete={deleteLessonHandler}
              // Undefined (not []) when the payload never loaded, so the strip
              // stays absent rather than asserting "in no Track" from ignorance.
              tracks={tracks}
              onOpenTrack={(trackId) => {
                setSelectedTrackId(trackId);
                setActiveTab('tracks');
              }}
              onOpenOriginal={openOriginalLesson}
            />
          ) : (
            <section className="min-h-0 overflow-y-auto flex items-center justify-center p-6 text-sm text-inkMuted">
              Select a {QUIZ_TERMS.lesson.singular} or {QUIZ_TERMS.drill.singular} from the tree.
            </section>
          )}

          {selectedDrill ? (
            /* feat-270 panels 3/4/9 — the detail pane's approval state. Provided
               at the pane, not inside each form, so all five item panels see the
               same state and none can be forgotten. */
            <ItemApprovalProvider value={itemApproval}>
              {renderRightPane()}
            </ItemApprovalProvider>
          ) : (
            <aside className="border-l border-softBorder bg-cream/40" />
          )}
        </div>
        {publishGuard && (
          <PublishGuardDialog
            containerTitle={publishGuard.title}
            report={publishGuard.report}
            onCancel={() => closePublishGuard(false)}
            onConfirm={() => closePublishGuard(true)}
          />
        )}
        {moveDialog && (
          <MoveDrillDialog
            drillTitle={moveDialog.drillTitle}
            currentLessonId={moveDialog.lessonId}
            units={units}
            glyphForKey={glyphForKey}
            onCancel={() => setMoveDialog(null)}
            onMove={moveDrillHandler}
          />
        )}
      </>
    );
  }

  return (
    // feat-231: `min-h-min` (min-height: min-content) is deliberate and is NOT
    // interchangeable with `min-h-0` here. The card must refuse to shrink below
    // app-bar + the grid's 480px pane floor; when the window is too short for
    // that, the card overflows its parent (which IS `min-h-0`) and the PAGE
    // scrolls — today's behavior — instead of `overflow-hidden` guillotining the
    // bottom of the panes. `min-h-0` on this element would do exactly that.
    <div className="bg-white border border-softBorder rounded-3xl shadow-sm overflow-hidden flex flex-col flex-1 min-h-min">
      {/* APP BAR */}
      <div className="bg-white border-b border-softBorder px-6 py-4 shrink-0 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold">Learn Module</h2>
          {/* feat-235: the subtitle is tab-specific. On Tracks it states what a
              track DOES, because that is the one thing an author cannot infer
              from the screen. */}
          <p className="text-sm text-inkMuted mt-0.5">
            {activeTab === 'tracks' ? (
              <>
                Organize {QUIZ_TERMS.lesson.plural} into {QUIZ_TERMS.track.plural}.{' '}
                {QUIZ_TERMS.track.plural} control what a crew member can start.
              </>
            ) : (
              <>
                Edit {QUIZ_TERMS.lesson.plural}, {QUIZ_TERMS.drill.plural}, {QUIZ_TERMS.question.plural} &amp; {QUIZ_TERMS.rehearsal.plural} shown in the Flutter Learn screen.
              </>
            )}
          </p>
        </div>
        {/* Two groups: links that navigate AWAY from this screen, then actions
            that operate ON the curriculum in front of you. The divider is the
            grouping cue — History belongs with the on-curriculum actions, not
            with the route-away links it used to sit among. */}
        <div className="flex items-center gap-4 shrink-0">
          <Link to="/quiz-translations" className="text-coral text-sm font-semibold hover:underline">Translations →</Link>
          <Link to="/quiz-review" className="text-coral text-sm font-semibold hover:underline">Review mode →</Link>
          {/*
            feat-270 AT-25 / AT-29. The entry point and the count are ONE
            conditional: with the flag off — or with the summary unloaded — this
            renders NOTHING, and the strip is exactly today's strip. AT-29 is
            "absent, not empty", so there is deliberately no branch here that
            renders the link with a `0` beside it.

            The badge is NEUTRAL, matching the tree chip and for the same reason
            (NOTES §3): pending is the frequent state, and colour spent on what
            fires fifty times an hour leaves nothing for the one that doesn't.
          */}
          {approval && approval.lessonsPending > 0 && (
            <Link
              to="/quiz-approvals"
              data-testid="approvals-entry"
              className="text-coral text-sm font-semibold hover:underline inline-flex items-center gap-1.5"
            >
              {APPROVAL_EDITOR_COPY.queueLink}
              <span className="bg-cream-dark text-inkMuted text-[10px] font-bold rounded-full px-1.5 py-0.5">
                {APPROVAL_EDITOR_COPY.headerBadge(
                  approval.lessonsPending,
                  approval.changesPending,
                )}
              </span>
            </Link>
          )}

          <span className="w-px h-6 bg-softBorder mx-1" aria-hidden />

          {/* feat-164: change history + per-item undo. Promoted from a muted
              text button to an outlined pill so it reads as a real action.
              `text-ink` at FULL strength is the point — the old `text-inkMuted`
              made it the least prominent thing in the bar, which is what the
              border + full ink together fix. It's a peer of "+ New Lesson"
              (secondary, outlined) without competing with the single solid-coral
              primary CTA. Labeled rather than icon-only, so the visible word IS
              the accessible name — deliberately NO aria-label here: one would
              override the visible word and break anything keyed to it, from
              name-based test locators to a speech-input user saying "History".
              (The slide-over dialog's own aria-label is a different thing and
              must stay — a dialog has no visible text to name it.) */}
          <button
            onClick={() => setHistoryOpen(true)}
            className="inline-flex items-center gap-1.5 text-sm font-semibold rounded-full border border-softBorder text-ink px-3 py-1.5 hover:bg-cream shrink-0"
            title="View change history and undo edits or deletions"
          >
            <History className="w-4 h-4" /> History
          </button>
          {activeTab === 'lessons' && renderImporter?.(importerCtx, 'appbar')}
          {/* One primary action per screen, same slot, same coral — it is the
              tab's own creator, not a second button beside it. */}
          {/* feat-235 — the app-bar creator disappears with the pane's on a
              `missing_no_tracks` tenant. Suppressing only one of the two would
              leave the laundering action reachable from the more prominent slot,
              which is the whole thing the suppression is for. */}
          {activeTab === 'tracks' ? (
            suppressesTrackCreation(defaultTrackState) ? null : (
              <button
                onClick={() => createTrackHandler('New ' + QUIZ_TERMS.track.singular)}
                disabled={!onReloadTracks}
                className="bg-coral text-white font-display font-semibold rounded-full px-4 py-2 text-sm hover:brightness-95 disabled:opacity-50"
              >
                + New {QUIZ_TERMS.track.singular}
              </button>
            )
          ) : (
            <button
              onClick={() => createLessonHandler('New ' + QUIZ_TERMS.lesson.singular)}
              className="bg-coral text-white font-display font-semibold rounded-full px-4 py-2 text-sm hover:brightness-95"
            >
              + New {QUIZ_TERMS.lesson.singular}
            </button>
          )}
        </div>
      </div>

      {/* feat-235 — SEGMENTED STRIP INSIDE THE CARD, not a fourth route. The
          editor had no strip before this: `Translations →` and `Review mode →`
          are separate routes (`App.tsx`), so this is a new control rather than
          a slot that was waiting. It is in-card because filing is interleaved
          with editing — the author reads a Lesson's Drills to decide which Track
          it belongs in, files it, and goes back — and a route change would drop
          the tenant selection and the scroll position every time.

          `role="tablist"`/`role="tab"` is the ARIA pattern for a control that
          swaps panes in place, which is what this is; the spec's word for the
          surface is "view", and neither name changes the markup. The counts are
          free (both lists are already loaded) and they answer "did my filing
          take?" without a click. */}
      <div className="flex gap-1 px-6 border-b border-softBorder shrink-0" role="tablist">
        {(
          [
            { key: 'lessons' as const, label: QUIZ_TERMS.lesson.plural, count: units.length },
            { key: 'tracks' as const, label: QUIZ_TERMS.track.plural, count: trackList.length },
          ]
        ).map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`text-[13.5px] font-bold px-3.5 py-2.5 -mb-px border-b-2 flex items-center gap-1.5 ${
              activeTab === tab.key
                ? 'text-navActive border-navActive'
                : 'text-inkMuted border-transparent hover:text-ink'
            }`}
          >
            {tab.label}
            <span
              className={`text-[11px] font-extrabold rounded-full px-1.5 py-0.5 ${
                activeTab === tab.key ? 'bg-navActiveBg text-navActive' : 'bg-cream-dark text-inkMuted'
              }`}
            >
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {activeTab === 'tracks' ? (
        <TracksTab
          tenantId={tenantId}
          units={units}
          tracks={trackList}
          unassignedLessonIds={unassignedLessonIds}
          loading={tracksLoading}
          error={tracksError}
          onReload={onReloadTracks ?? onReload}
          selectedTrackId={selectedTrackId}
          onSelectTrack={setSelectedTrackId}
          onCreateTrack={createTrackHandler}
          glyphForKey={glyphForKey}
          defaultTrackState={defaultTrackState}
          // feat-242 — the Map's Lesson chips jump to the Lessons tab, the exact
          // reverse of the jump `LessonTracksStrip`'s Track chips already do
          // above. Navigation only; the Map originates no write (AC18).
          //
          // NOTE this is the ONLY change this feature makes to this file. The
          // Editor/Map toggle deliberately does NOT live in the card header —
          // see the view bar in `TracksTab.tsx` and OPEN-QUESTIONS Q2.
          onOpenLesson={(lessonId) => {
            setSelectedUnitId(lessonId);
            setActiveTab('lessons');
          }}
        />
      ) : (
        body
      )}
      <CurriculumHistoryPanel
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        tenantId={tenantId}
        chain={historyChain}
        onReverted={onReload}
      />
    </div>
  );
};

/**
 * feat-107 Slice A — the /quiz-management mount. Owns tenant + fetch + reload,
 * and composes the master-detail editor. This default export is the agreed
 * SHARED SEAM: Slice B wires its paste-from-Notion launcher in by importing
 * `PasteImportLauncher` and passing `renderImporter` below.
 *
 * THE `/quiz-management` URL IS NOT STALE — it is EXCLUDED BY NAME (feat-198
 * ADR 0001 §D-M). feat-198 renamed the tables, columns and API paths, but the
 * four browser routes (`/quiz-management`,
 * `/quiz-management/rehearsal-test/:rehearsalId`, `/quiz-review`,
 * `/quiz-translations`) were deliberately left alone: a bookmarked URL is user
 * state, and renaming them would be a user-visible change in a feature scoped
 * as zero-visual-change. So this page renders Lessons and Drills at a URL that
 * says "quiz". That divergence PREDATES feat-198 — feat-107 migrated the
 * display layer and left the URL behind — and the exclusion preserves it rather
 * than creating it. Adding `/learn-*` routes with redirects is queued as
 * feat-104 work, where it can be designed instead of inherited.
 */
const QuizContentManagement: React.FC = () => {
  const { selectedTenant } = useTenant();
  const [content, setContent] = useState<QuizContent | null>(null);
  const [availableRoles, setAvailableRoles] = useState<AvailableRoles>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // feat-235 — the tracks payload is fetched and FAILS SEPARATELY from the
  // curriculum. `tracks === undefined` means "never loaded", which is different
  // from "loaded and empty": the Lessons tab's membership strip renders only in
  // the second case, so a failed fetch never asserts "in no Track" from
  // ignorance. Same graceful-degradation shape as the Revel POS fetch in
  // ShiftTranscriptView.
  const [tracks, setTracks] = useState<LearnTrack[] | undefined>(undefined);
  const [unassignedLessonIds, setUnassignedLessonIds] = useState<string[]>([]);
  const [tracksLoading, setTracksLoading] = useState(false);
  const [tracksError, setTracksError] = useState<string | null>(null);
  const [defaultTrackState, setDefaultTrackState] = useState<DefaultTrackState>('ok');

  /**
   * feat-270 AT-25 / AT-29 — the approval summary (ADR-B §3.1).
   *
   * ONE call, and it is the only one the editor page needs on load: it carries
   * `approval_required`, `can_approve`, the header counts and every lesson's
   * state. ADR-A evidence T6 puts the whole-tenant derivation at ~1.5ms, which is
   * what makes it safe to sit on this page's existing load.
   *
   * It FAILS SEPARATELY from the curriculum, the same graceful-degradation shape
   * the tracks payload above uses. `undefined` means "never loaded", which is not
   * the same as "loaded, nothing pending" — and the difference matters here more
   * than it does for tracks, because §10.10 makes the absence of a marker a
   * CLAIM: a panel that silently draws no chips is indistinguishable from
   * "everything matches", and it would be telling an editor their draft is live
   * when it may not be. So a failed fetch renders NO chips and NO badge rather
   * than zeros — it asserts nothing, where a `0` would assert a measurement.
   */
  const [approval, setApproval] = useState<ApprovalSummary | undefined>(undefined);

  useEffect(() => {
    if (!selectedTenant) {
      setApproval(undefined);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const summary = await getApprovalSummary(selectedTenant);
        if (!cancelled) setApproval(summary);
      } catch {
        // Absence, not a zero. See the note above.
        if (!cancelled) setApproval(undefined);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedTenant]);

  /**
   * AT-29 — with the flag FALSE the whole surface is ABSENT: no badge, no entry
   * point, no per-lesson state, and the editor looks exactly as it does today.
   * One condition, checked once, feeding every consumer below.
   */
  const approvalActive = approval?.approval_required === true;

  /** AT-25's per-lesson half. Null when the surface is absent (AT-29). */
  const approvalByLessonId = useMemo(() => {
    if (!approvalActive || !approval) return null;
    const map = new Map<string, LessonApprovalRow>();
    for (const lesson of approval.lessons) {
      map.set(lesson.lesson_id, {
        state: lesson.state,
        pendingCount: lesson.pending_item_count,
        approvedBy: lesson.approved_by,
        approvedAt: lesson.approved_at,
      });
    }
    return map;
  }, [approvalActive, approval]);

  const reloadTracks = useCallback(async () => {
    if (!selectedTenant) return;
    setTracksLoading(true);
    setTracksError(null);
    try {
      const payload = await getLearnTracks(selectedTenant);
      setTracks(payload.tracks);
      setUnassignedLessonIds(payload.unassigned_lesson_ids);
      // A backend that predates the field sends nothing; `ok` is the right
      // reading of "cannot tell", because accusing a healthy tenant on the
      // strength of a missing key would suppress its create button for no reason.
      setDefaultTrackState(payload.default_track_state ?? 'ok');
    } catch (e) {
      setTracksError(e instanceof Error ? e.message : String(e));
    } finally {
      setTracksLoading(false);
    }
  }, [selectedTenant]);

  const reload = useCallback(async () => {
    if (!selectedTenant) return;
    setLoading(true);
    setError(null);
    try {
      const [data, roles] = await Promise.all([
        getQuizContent(selectedTenant),
        // feat-251 AC21: still isolated from the curriculum fetch so a roles
        // failure never blanks the whole page — but it resolves to `undefined`,
        // not `[]`, so the rehearsal editor can say "couldn't load" instead of
        // asserting this tenant has no roles. See `AvailableRoles` above.
        listRolesWithActivePrompt(selectedTenant).catch(
          () => undefined as AvailableRoles,
        ),
      ]);
      setContent(data);
      setAvailableRoles(roles);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [selectedTenant]);

  useEffect(() => {
    if (selectedTenant) {
      reload();
      reloadTracks();
    } else {
      setContent(null);
      setTracks(undefined);
      setUnassignedLessonIds([]);
      setDefaultTrackState('ok');
    }
  }, [selectedTenant, reload, reloadTracks]);

  // ── SLICE B WIRE-IN (feat-107-frontend-b) ────────────────────────────────
  // The paste-from-Notion launcher renders in both the app-bar and the
  // no-Lessons empty-state slots; it owns its own 3-step wizard modal and calls
  // ctx.onImported (→ reload) after a successful commit.
  const renderImporter: ImporterRenderer = (ctx, variant) => (
    <PasteImportLauncher ctx={ctx} variant={variant} />
  );

  return (
    // feat-231 — THE fix. Everything else in this feature is plumbing that was
    // already in place and inert.
    //
    // `min-h-screen` gave the page a FLOOR and no CEILING, so the card grew to
    // the full height of its content and the browser window scrolled the whole
    // three-pane block as one unit. `h-screen` + a flex column gives the card a
    // real height to divide up. ScorecardNavigation is left untouched (it is
    // shared by ~a dozen routes) — as a flex item its default
    // `min-height: auto` already keeps it at its natural height, and its own
    // `sticky top-0` is preserved for the short-window case below.
    <div className="h-screen flex flex-col bg-cream">
      <ScorecardNavigation />
      <div className="max-w-[1320px] w-full mx-auto px-6 py-8 flex-1 min-h-0 flex flex-col">
        {!selectedTenant ? (
          <div className="p-4 bg-warning-surface border border-gold/30 rounded-2xl text-sm text-ink">
            No tenant selected. Return to a scorecard page and pick a tenant first.
          </div>
        ) : (
          /* feat-270 — the gate, for panels too deep to thread a prop to. The
             provider only appears when the server says the flag is on, and the
             context default is OFF, so AT-29's "absent, not empty" holds by
             construction rather than by every consumer remembering it. */
          <ApprovalGateProvider
            approvalRequired={approvalActive}
            canApprove={approval?.can_approve === true}
          >
              <CurriculumEditor
              approval={
                approvalByLessonId && approval
                  ? {
                      byLessonId: approvalByLessonId,
                      lessonsPending: approval.lessons_pending,
                      changesPending: approval.changes_pending,
                    }
                  : null
              }
              tenantId={selectedTenant}
              content={content}
              loading={loading}
              error={error}
              availableRoles={availableRoles}
              onReload={reload}
              renderImporter={renderImporter}
              tracks={tracks}
              unassignedLessonIds={unassignedLessonIds}
              tracksLoading={tracksLoading}
              tracksError={tracksError}
              onReloadTracks={reloadTracks}
              defaultTrackState={defaultTrackState}
              />
          </ApprovalGateProvider>
        )}
      </div>
    </div>
  );
};

export default QuizContentManagement;
